import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useClientWorkGuard } from './post-creator/useClientWorkGuard'
import { useMediaUploadQueue, hasUnfinishedMediaUploads, restoreUploadPlaceholder } from './post-creator/useMediaUploadQueue'
import { Link, useNavigate, useParams, useLocation } from 'react-router'
import { useQueryClient, useQuery, useMutation, useQueries, useInfiniteQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { useAuth } from '../hooks/useAuth'
import { useSuggestedBets } from '../hooks/useSuggestedBets'
import { useTranscriptionJobPolling } from '../hooks/useTranscriptionJobPolling'
import { useCanAddCreators } from '../hooks/useCanAddCreators'
import api, { aiApi, feedOpsApi, kambiApi, mediaLibraryApi, organizationApi, postApi, surfacesApi, tagApi } from '../services/api'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import { PRODUCT_KEYS } from '../config/studioProducts'
import CreatorPicker from './CreatorPicker'
import TagSelector from './TagSelector'
import AdvancedSettingsSection from './post-creator/AdvancedSettingsSection'
import { normalizeCardPresentation } from './parlays/cardPresentationModel'
import BetSection from './post-creator/BetSection'
import ContentSection from './post-creator/ContentSection'
import CtaSection from './post-creator/CtaSection'
import MediaSection from './post-creator/MediaSection'
import PostPreviewPanel from './post-creator/PostPreviewPanel'
import PublishToSection from './post-creator/PublishToSection'
import ContentStep from './post-creator/ContentStep'
import PublishStep from './post-creator/PublishStep'
import MarketStep from './post-creator/MarketStep'
import PostComposerLayout from './post-creator/PostComposerLayout'
import PromptPresetNameDialog from './post-creator/PromptPresetNameDialog'
import { imageGenerationErrorMessage, restoreImageGenerationJob } from './post-creator/imageGenerationJob'
import { useImageGenerationJob } from './post-creator/useImageGenerationJob'
import { useReferenceImages } from './post-creator/useReferenceImages'
import RegulationContentSection from './post-creator/RegulationContentSection'
import { regulationOverrideDiffers, sameTagSet } from './post-creator/regulationOverrides'
import RegulationTargetSection from './post-creator/RegulationTargetSection'
import ModerationStatusPanel from './post-creator/ModerationStatusPanel'
import { parseModerationBlock } from './moderation/moderationReviewModel'
import { moderationEditNotice, moderationPublishNotice } from './moderation/moderationHoldStatus'
import { assetAttachBlocked } from './moderation/assetModerationModel'
import {
  describeSkippedResponse,
  parseRegulationAvailabilityError,
  partialPublishConfirmLabel,
  partialPublishMessage,
  regulationNoticesByOfferingId,
  regulationShortfallMessage,
  validationRegulationShortfall,
} from './post-creator/regulationAvailability'
import { marketSearchScope } from './post-creator/marketSearchScope'
import {
  AI_PROMPT_TEMPLATE_KIND,
  DEFAULT_AI_IMAGE_SIZE,
  aiAssetTitle,
  aiPromptFallbackName,
  aiPromptFieldsFromTemplate,
  aiPromptTemplatePayload,
  aiPromptTextFromTemplate,
  buildImagePrompt,
  normalizeAiPromptFields,
} from './post-creator/imagePrompt'
import composerStyles from './post-creator/PostComposer.module.css'

import {
  Breadcrumbs,
  Button,
  ErrorSummary,
  FormActions,
  FormField,
  ModalShell,
  SegmentedControl,
  ConfirmDialog,
  StatePanel,
} from './common'
import MediaPreviewModal from './common/MediaPreviewModal'
import { normalizeFeedPreviewItem } from '../utils/feedPreviewItem'
import { parseOutcomeId } from '../utils/odds'
import { buildRegulationLookup, regulationDisplayLabel } from '../utils/regulationLabels'
import { normalizePostPreviewMedia } from '../utils/postPreviewMedia'
import { extractErrorMessage } from '../lib/useApiMutation'
import {
  attachAssetToState,
  captionLabelForLang,
  captionLangFromFilename,
  createCaptionTrack,
  createMediaAttachment,
  createUnavailableVideoAnalysis,
  describeCaptionTrackIssues,
  mediaUploadContentType,
  createVideoAnalysisFromError,
  createVideoAnalysisFromJob,
  isRetryableVideoAnalysis,
  normalizeCaptionTracks,
  serializeCaptionTracks,
  setMediaCaptionTracks,
  validateCaptionUploadFile,
  removeMediaAtIndex,
} from './post-creator/mediaAttachments'
import {
  blockImplicitFormSubmit,
  isNumericExternalId,
  parseAiJson,
  parseNumericOutcomeId,
  parseStoredJson,
  persistStoredJson,
  removeStoredValue,
  useBlobPreviewUrlCleanup,
} from './post-creator/composerShared'
import {
  applyValidatedDefaultWagerType,
  buildFeedCreatePayload,
  buildFeedUpdatePayload,
  buildAuthoredWagerPreviewSelectionSet,
  createComposerAiReview,
  createComposerAdjunctState,
  formatDateTimeInput,
  getPostComposerProviderKind,
  isComposerAdjunctInProgress,
  normalizeComposerAiReview,
  normalizeComposerReferenceImages,
  normalizePostComposerState,
  reconcileAuthoredWagerEntries,
  resolvePostComposerCopyCountEnabled,
  serializePostComposerCTA,
  useComposerDocument,
  validatePostComposerStep,
  validateAuthoredWagerEntries,
} from './post-creator/postComposerModel'
import { kambiOutcomePreviewLeg } from './post-creator/kambiOutcomePreview'
import {
  POST_COMPOSER_SECTION_REGISTRY,
  buildWorkflowSteps,
  renderComposerSections,
  sectionLabel,
} from './post-creator/postComposerSections'

const SCROLLABLE_OVERFLOW_PATTERN = /(auto|scroll)/
const UPPERCASE_LETTERS_PATTERN = /([A-Z])/g

const DRAFT_STORAGE_PREFIX = 'studio-post-draft-v1'
const UI_PREFS_STORAGE_PREFIX = 'studio-post-composer-prefs-v1'
const DEFAULT_ADVANCED_SECTION_EXPANDED = false
const TRANSCRIPTION_TIMEOUT_MS = 13 * 60 * 1000
const TAG_SUGGESTION_DEBOUNCE_MS = 10_000
const EMPTY_OFFERINGS = []
// Keep aligned with maxAITextImageURLs in apps/studio/api/services/ai_text_provider.go.
export const MAX_SUGGESTION_IMAGES = 4

function isImageMedia(media) {
  return [media?.type, media?.asset_type].some((value) => {
    const type = String(value || '').toLowerCase()
    return type === 'image' || type.startsWith('image/')
  })
}

function buildDraftKey(orgSlug, mode, postId) {
  if (!orgSlug) return null
  return `${DRAFT_STORAGE_PREFIX}:${orgSlug}:${mode}:${postId || 'new'}`
}

function buildUiPrefsKey(orgSlug) {
  if (!orgSlug) return null
  return `${UI_PREFS_STORAGE_PREFIX}:${orgSlug}`
}

function normalizeSelectedCreator(creator) {
  if (!creator) return null

  return {
    id: creator.id || creator.value || '',
    display_name: creator.display_name || creator.label || '',
    avatar_url: creator.avatar_url || '',
    allowed_offerings: creator.allowed_offerings || [],
  }
}

function createUploadedMediaFromStoredMedia(storedMedia = []) {
  return (storedMedia || [])
    .map((media) => {
      if (!media) return null
      if (!media.url && !media.mediaId && (!media.previewUrl || media.previewUrl.startsWith('blob:'))) return restoreUploadPlaceholder(media)
      const normalized = normalizePostPreviewMedia(media)
      if (!normalized) return null
      const type = normalized.type || media.type || media.asset_type

      return {
        id: media.mediaObjectId || media.id || crypto.randomUUID(),
        mediaObjectId: media.mediaObjectId || media.id || null,
        type,
        name: media.name || media.title || 'media',
        mediaId: media.mediaId || media.media_id || null,
        objectKey: media.objectKey || media.object_key,
        sourceObjectKey: media.sourceObjectKey || media.source_object_key,
        previewUrl: media.previewUrl || media.url || normalized.url,
        url: media.url || normalized.fallback_url || normalized.url,
        hlsManifestUrl: normalized.hls_manifest_url,
        fallbackUrl: normalized.fallback_url,
        posterUrl: normalized.poster_url,
        metadata: normalized.metadata || media.metadata,
        uploading: false,
        ...(media.uploadPhase ? { uploadPhase: 'ready', uploadSize: media.uploadSize } : {}),
        ...(type === 'video'
          ? {
            captionTracks: normalizeCaptionTracks(media.captionTracks ?? media.captions ?? media.metadata?.captions)
              .map((track) => createCaptionTrack(track)),
          }
          : {}),
      }
    })
    .filter(Boolean)
}

function sameIdSet(a = [], b = []) {
  if (a.length !== b.length) return false
  const other = new Set(b)
  return a.every((id) => other.has(id))
}

function hasComposerContent(
  formData,
  uploadedMedia,
  selectedCreator,
  defaultSkipApproval = false,
  defaultTargetOfferings = [],
  defaultSurfaceIds = null,
  adjunct = createComposerAdjunctState(),
) {
  return Boolean(
    formData.userId ||
    selectedCreator?.id ||
    formData.text.trim() ||
    formData.title?.trim() ||
    formData.subtitle?.trim() ||
    formData.selectionIds.length ||
    formData.kambiOutcomeIds.length ||
    formData.providerSelectionIds.length ||
    (formData.targetOfferings.length && !sameIdSet(formData.targetOfferings, defaultTargetOfferings)) ||
    formData.tagIds.length ||
    formData.mediaObjects.length ||
    uploadedMedia.length ||
    formData.ctaKind !== 'betslip' ||
    formData.isHidden ||
    formData.isPinned ||
    (Array.isArray(formData.surfaceIds) && (
      Array.isArray(defaultSurfaceIds)
        ? !sameIdSet(formData.surfaceIds, defaultSurfaceIds)
        : formData.surfaceIds.length > 0
    )) ||
    formData.boostFactor !== 1 ||
    formData.postType !== 'organic' ||
    Boolean(formData.expiresAt) ||
    Boolean(formData.scheduledFor) ||
    formData.promoPriority !== 0 ||
    Boolean(formData.interleaveEvery) ||
    formData.skipApproval !== defaultSkipApproval ||
    isComposerAdjunctInProgress(adjunct)
  )
}

function restoreDraftFormData(savedFormData = {}) {
  return normalizePostComposerState(savedFormData)
}

function mergeTranscriptionOutput(currentValue, outputPayload = {}) {
  return {
    ...currentValue,
    transcript: outputPayload?.transcript || currentValue.transcript,
    caption: outputPayload?.caption || currentValue.caption,
    captions: outputPayload?.caption
      ? [outputPayload.caption]
      : currentValue.captions,
    tags: Array.isArray(outputPayload?.tags) ? outputPayload.tags : currentValue.tags,
    // The backend always ships summary as a map, {} when extraction produced
    // nothing — only a populated summary should replace what we have.
    betSummary: outputPayload?.summary && Object.keys(outputPayload.summary).length > 0
      ? outputPayload.summary
      : currentValue.betSummary,
  }
}

function getStoredMediaContentType(media) {
  const metadata = media?.metadata || {}
  const contentType = media?.contentType || metadata.content_type || metadata.contentType
  if (contentType) return contentType
  if (String(media?.type || '').startsWith('video/')) return media.type
  return media?.type === 'video' ? 'video/mp4' : media?.type || 'video/mp4'
}

function normalizeDraftTranscriptionJobs(savedJobs, uploadedMedia = []) {
  const jobRecords = Array.isArray(savedJobs)
    ? savedJobs
    : Object.entries(savedJobs || {}).map(([mediaId, entry]) => ({ ...entry, mediaId }))
  const mediaById = new Map(uploadedMedia.map((media) => [media.id, media]))
  const validStatuses = new Set([
    'polling',
    'retrying',
    'completed',
    'unavailable',
    'invalid_input',
    'failed_retryable',
    'too_large',
  ])

  return jobRecords.reduce((jobs, record) => {
    if (!record?.mediaId || !mediaById.has(record.mediaId) || !validStatuses.has(record.status)) return jobs

    const media = mediaById.get(record.mediaId)
    const parsedStartedAt = typeof record.startedAt === 'number'
      ? record.startedAt
      : Date.parse(record.startedAt || '')
    jobs[record.mediaId] = {
      jobId: record.jobId || null,
      orgSlug: record.orgSlug || '',
      status: record.status,
      error: record.error || '',
      errorCode: record.errorCode || null,
      retryable: record.retryable === true,
      attempt: record.attempt || 1,
      retryOf: record.retryOf || null,
      mediaUrl: record.mediaUrl || media?.url || media?.previewUrl || '',
      title: record.title || media?.name || 'video',
      contentType: record.contentType || getStoredMediaContentType(media),
      startedAt: Number.isNaN(parsedStartedAt) ? Date.now() : parsedStartedAt,
      completedAt: record.completedAt || null,
    }
    return jobs
  }, {})
}

// draftMediaEntry is what a composer draft stores of one attached media item.
function draftMediaEntry(media) {
  return {
    id: media.id,
    mediaObjectId: media.mediaObjectId,
    type: media.type,
    name: media.name,
    uploadPhase: media.uploadPhase,
    uploadSize: media.uploadSize,
    mediaId: media.mediaId,
    objectKey: media.objectKey,
    sourceObjectKey: media.sourceObjectKey,
    url: media.url,
    previewUrl: media.url || (String(media.previewUrl || '').startsWith('blob:') ? null : media.previewUrl),
    ...(Array.isArray(media.captionTracks) ? { captionTracks: media.captionTracks } : {}),
  }
}

// editDraftSignature identifies an edit page's content as a draft stores
// it, so a draft can be told apart from the post as loaded.
function editDraftSignature(formData, uploadedMedia) {
  return JSON.stringify({ formData, uploadedMedia: (uploadedMedia || []).map(draftMediaEntry) })
}

function selectNewestDraft(localDraft, serverDraft) {
  if (!localDraft) return serverDraft || null
  if (!serverDraft) return localDraft || null

  const localSavedAt = Date.parse(localDraft.savedAt || '')
  const serverSavedAt = Date.parse(serverDraft.savedAt || '')
  if (Number.isNaN(localSavedAt)) return serverDraft
  if (Number.isNaN(serverSavedAt)) return localDraft
  return serverSavedAt > localSavedAt ? serverDraft : localDraft
}

function cryptoSelectionLabel(selection) {
  const meta = selection?.selection_metadata || {}
  return meta.team_abbrev || meta.team_name || selection?.name || 'Contract'
}

function providerContractLabel(selection) {
  const meta = selection?.selection_metadata || {}
  return selection?.name || meta.contract_title || meta.team_abbrev || meta.team_name || meta.individual_name || 'Contract'
}

function buildProviderContractPreviewLeg(selection, providerKey) {
  const meta = selection.selection_metadata || {}
  return {
    event: {
      name: selection.event_name || meta.event_name || '',
      start_time: selection.event_start,
    },
    market: {
      name: selection.market_name || meta.market_title || '',
      type: selection.market_type || meta.market_type || '',
    },
    selection: {
      name: selection.name || cryptoSelectionLabel(selection),
      outcome_id: selection.external_id,
      provider_selection_id: selection.external_id,
      provider_payload: {
        provider: providerKey,
        contract_id: selection.external_id,
        ...meta,
      },
    },
    provider_payload: {
      provider: providerKey,
      contract_id: selection.external_id,
      ...meta,
    },
    odds: selection.odds_american
      ? { american: selection.odds_american }
      : selection.odds_decimal
        ? { decimal: selection.odds_decimal }
        : null,
  }
}

function StandardPostCreator({ mode = 'create' }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: postIdParam } = useParams()
  const composerLaunchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const initialSurfaceSlug = composerLaunchParams.get('surface') || ''
  const launchedForParlay = composerLaunchParams.get('content') === 'parlay'
  const queryClient = useQueryClient()
  const { currentOrg, hasProductAccess } = useOrganization()
  const canCreateCreators = useCanAddCreators()
  const isInternalRole = (
    ROLE_HIERARCHY[currentOrg?.role || ROLES.VIEWER] || 0
  ) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const canConfigureSurface = isInternalRole
  // Anyone who may publish posts publishes directly: Wager does not review
  // operator posts. Only Wager staff can still route a post to review.
  const canPublishNow = Boolean(hasProductAccess?.(PRODUCT_KEYS.FEED, 'publish'))
  const canSendToReview = isInternalRole
  // Regulation-specific copy is saved through the feed-ops edit route, which
  // needs Feed configure or operate (the API refuses publish-only editors).
  const canTailorRegulationCopy = Boolean(
    hasProductAccess?.(PRODUCT_KEYS.FEED, 'configure') || hasProductAccess?.(PRODUCT_KEYS.FEED, 'operate'),
  )
  const isEditMode = mode === 'edit' && !!postIdParam
  // Deferred-video submission needs a surface id -> slug map to route the
  // operator to the queue their post actually landed in, so this loads
  // whenever a create-mode composer could submit, not only when the
  // composer was deep-linked into a specific launch surface. It shares the
  // exact query key/fn PublishToSection already uses for the same catalog,
  // so this is a second observer of that one convention, not a new one.
  const launchSurfacesQuery = useQuery({
    queryKey: ['surfaces', currentOrg?.slug],
    queryFn: () => surfacesApi.list(currentOrg.slug),
    enabled: Boolean(currentOrg?.slug && !isEditMode),
    staleTime: 30_000,
  })
  const launchSurface = useMemo(
    () => (launchSurfacesQuery.data?.items || []).find((surface) => (
      surface.slug === initialSurfaceSlug && surface.is_enabled
    )) || null,
    [initialSurfaceSlug, launchSurfacesQuery.data?.items],
  )
  const launchSurfaceLabel = launchSurface?.name || initialSurfaceSlug
  const [loading, setLoading] = useState(isEditMode)
  // Edit mode only: why the post could not be loaded. The composer is not
  // rendered then, so a missing or unreadable post can never be saved over.
  const [loadError, setLoadError] = useState(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const { authored: formData, adjunct, setAuthored: setFormData, setAdjunctField } = useComposerDocument()
  const [uploadedMedia, setUploadedMedia] = useState([])
  const mediaUploads = useMediaUploadQueue(setUploadedMedia)
  const [previewMedia, setPreviewMedia] = useState(null)
  const [kambiValidation, setKambiValidation] = useState(null)
  const [regulationAvailability, setRegulationAvailability] = useState(null)
  const [partialPublishPrompt, setPartialPublishPrompt] = useState(null)
  const [providerSelectionDetails, setProviderSelectionDetails] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The last save's enforce-mode moderation refusal, kept inline so the
  // draft stays in place while the author fixes it.
  const [moderationBlock, setModerationBlock] = useState(null)
  const [selectedCreator, setSelectedCreator] = useState(null)
  // The loaded post's creator in edit mode, where the creator is not picked
  // again: the preview shows who the post is by.
  const [editCreator, setEditCreator] = useState(null)
  const [pendingDraft, setPendingDraft] = useState(null)
  const [draftReady, setDraftReady] = useState(false)
  // Set once a create or update succeeds. The composer stays mounted until
  // the route transition to Manage posts commits (a lazy route can keep it
  // up for a while), and clearing isSubmitting re-runs the draft autosave
  // with the submitted fields; this keeps a published post from being saved
  // back as a draft and offered on the next Create post.
  const submissionCompletedRef = useRef(false)
  const [advancedSectionExpanded, setAdvancedSectionExpanded] = useState(DEFAULT_ADVANCED_SECTION_EXPANDED)
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false)
  const [assetSearch, setAssetSearch] = useState('')
  const [debouncedAssetSearch, setDebouncedAssetSearch] = useState('')
  const [mediaActivePanel, setMediaActivePanel] = useState(null)
  const [saveUploadsToLibrary, setSaveUploadsToLibrary] = useState(false)
  const [captionGuidance, setCaptionGuidance] = useState('')
  const publishSurfaceContextKey = `${currentOrg?.slug || ''}:${isEditMode ? '' : initialSurfaceSlug}`
  const [surfaceAvailability, setSurfaceAvailability] = useState({
    contextKey: '',
    available: false,
  })
  // A surface applied from the launch URL is route context, not authored
  // content. Keep it as the pristine baseline until the operator changes it.
  const [launchSurfaceBaseline, setLaunchSurfaceBaseline] = useState({
    contextKey: '',
    ids: null,
  })
  const launchSurfaceIdsBaseline = launchSurfaceBaseline.contextKey === publishSurfaceContextKey
    ? launchSurfaceBaseline.ids
    : null
  const hasEnabledSurfaces = (
    surfaceAvailability.contextKey === publishSurfaceContextKey
    && surfaceAvailability.available
  )
  const creatorLookupKey = `${currentOrg?.slug || ''}:${formData.userId || ''}`
  const [creatorValidity, setCreatorValidity] = useState({ lookupKey: '', valid: true })
  const creatorSelectionValid = (
    isEditMode
    || !formData.userId
    || (creatorValidity.lookupKey === creatorLookupKey && creatorValidity.valid)
  )
  const handleSurfaceAvailabilityChange = useCallback((available) => {
    setSurfaceAvailability((currentValue) => (
      currentValue.contextKey === publishSurfaceContextKey
      && currentValue.available === available
        ? currentValue
        : { contextKey: publishSurfaceContextKey, available }
    ))
  }, [publishSurfaceContextKey])
  const handleInitialSurfaceApplied = useCallback((surfaceIds) => {
    setLaunchSurfaceBaseline((currentValue) => (
      currentValue.contextKey === publishSurfaceContextKey
      && sameIdSet(currentValue.ids || [], surfaceIds)
        ? currentValue
        : { contextKey: publishSurfaceContextKey, ids: surfaceIds }
    ))
  }, [publishSurfaceContextKey])
  const handleCreatorValidityChange = useCallback((valid) => {
    setCreatorValidity((currentValue) => (
      currentValue.lookupKey === creatorLookupKey && currentValue.valid === valid
        ? currentValue
        : { lookupKey: creatorLookupKey, valid }
    ))
  }, [creatorLookupKey])
  const handleResolvedCreator = useCallback((creator) => {
    setSelectedCreator(normalizeSelectedCreator(creator))
  }, [])
  const {
    aiReview,
    transcriptionJobs,
    imagePrompt,
    imagePromptFields,
    selectedPromptPresetId,
    referenceImages,
    generatedImageReview,
    imageGenerationJob,
    revisionPrompt,
    generatedAssetIds,
  } = adjunct
  const setAiReview = useCallback((value) => setAdjunctField('aiReview', value), [setAdjunctField])
  const setTranscriptionJobs = useCallback((value) => setAdjunctField('transcriptionJobs', value), [setAdjunctField])
  const setImagePrompt = useCallback((value) => setAdjunctField('imagePrompt', value), [setAdjunctField])
  const setImagePromptFields = useCallback((value) => setAdjunctField('imagePromptFields', value), [setAdjunctField])
  const setSelectedPromptPresetId = useCallback((value) => setAdjunctField('selectedPromptPresetId', value), [setAdjunctField])
  const setReferenceImages = useCallback((value) => setAdjunctField('referenceImages', value), [setAdjunctField])
  const [promptPresetNameRequest, setPromptPresetNameRequest] = useState(null)
  const setGeneratedImageReview = useCallback((value) => setAdjunctField('generatedImageReview', value), [setAdjunctField])
  const setImageGenerationJob = useCallback((value) => setAdjunctField('imageGenerationJob', value), [setAdjunctField])
  const setRevisionPrompt = useCallback((value) => setAdjunctField('revisionPrompt', value), [setAdjunctField])
  const setGeneratedAssetIds = useCallback((value) => setAdjunctField('generatedAssetIds', value), [setAdjunctField])
  const [activeStep, setActiveStep] = useState(launchedForParlay ? 'market' : 'content')
  const [regulationCopy, setRegulationCopy] = useState({})
  // Read synchronously: defaulting to one-page and correcting after the prefs
  // effect renders the wrong mode first, which both flashes and lets the
  // one-page scroll spy move the guided composer off its first step. Edit mode
  // always opens on one page and never consumes the remembered choice.
  const [composerMode, setComposerMode] = useState(
    () => (!isEditMode && parseStoredJson(buildUiPrefsKey(currentOrg?.slug), {}).composerMode === 'guided'
      ? 'guided'
      : 'one-page'),
  )
  const composerModeRef = useRef('one-page')
  composerModeRef.current = composerMode
  const [uiPrefsReadyKey, setUiPrefsReadyKey] = useState('')
  const [workflowError, setWorkflowError] = useState(null)
  const [marketSearchQuery, setMarketSearchQuery] = useState('')
  const [marketSearchScopeChoice, setMarketSearchScopeChoice] = useState('')
  const [onePageBetExpanded, setOnePageBetExpanded] = useState(launchedForParlay)
  const { organizations: authOrganizations } = useAuth()
  const userOrgs = authOrganizations || []
  const marketValidationRef = useRef(null)
  const initialEditFormDataRef = useRef(null)
  const workflowErrorRef = useRef(null)
  const mobilePreviewTriggerRef = useRef(null)
  const mediaLibraryPrefillAppliedRef = useRef(false)
  const removedUploadPlaceholderIdsRef = useRef(new Set())
  const retryingTranscriptionMediaIdsRef = useRef(new Set())
  const defaultPromptPresetAppliedRef = useRef(false)
  const latestTagSuggestionContextRef = useRef('')
  const observedTagSuggestionContextRef = useRef('')
  const requestedTagSuggestionContextRef = useRef('')
  // An edit page's content as loaded (editDraftSignature): a draft equal to
  // it is not offered or stored. The restore effect reads the loaded content
  // through latestDraftInputRef so it does not rerun on every keystroke.
  const editBaselineRef = useRef(null)
  const latestDraftInputRef = useRef({ formData, uploadedMedia })
  latestDraftInputRef.current = { formData, uploadedMedia }
  useEffect(() => {
    if (!launchedForParlay) return undefined
    const frame = window.requestAnimationFrame(() => {
      if (composerMode === 'one-page') {
        const marketSection = document.getElementById('post-composer-section-market')
        marketSection?.scrollIntoView?.({ block: 'start' })
        marketSection?.querySelector('button[aria-expanded]')?.focus({ preventScroll: true })
        return
      }
      document.getElementById('post-composer-market-heading')?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [composerMode, launchedForParlay])


  // The scroll spy is one-page only. It must also re-check on the frame it
  // runs, because a mode switch can land between scheduling and execution.
  useEffect(() => {
    if (composerMode !== 'one-page') return undefined

    const jumpBar = document.querySelector('[aria-label="Post sections"]')
    let scrollContainer = jumpBar?.parentElement
    while (scrollContainer && scrollContainer !== document.body) {
      if (SCROLLABLE_OVERFLOW_PATTERN.test(window.getComputedStyle(scrollContainer).overflowY)) break
      scrollContainer = scrollContainer.parentElement
    }
    const sections = POST_COMPOSER_SECTION_REGISTRY
      .map((entry) => document.getElementById(entry.focusId))
      .filter(Boolean)
    if (!sections.length) return undefined

    let frameId = null
    const updateActiveSection = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(() => {
        frameId = null
        const activationLine = (jumpBar?.getBoundingClientRect().bottom || 0) + 32
        let currentSection = sections[0]
        for (const section of sections) {
          if (section.getBoundingClientRect().top > activationLine) break
          currentSection = section
        }
        if (
          scrollContainer
          && scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 2
        ) {
          currentSection = sections[sections.length - 1]
        }
        const nextStep = currentSection.id.replace('post-composer-section-', '')
        if (composerModeRef.current !== 'one-page') return
        setActiveStep((currentStep) => currentStep === nextStep ? currentStep : nextStep)
      })
    }

    updateActiveSection()
    scrollContainer?.addEventListener('scroll', updateActiveSection, { passive: true })
    window.addEventListener('scroll', updateActiveSection, { passive: true })
    window.addEventListener('resize', updateActiveSection)
    return () => {
      scrollContainer?.removeEventListener('scroll', updateActiveSection)
      window.removeEventListener('scroll', updateActiveSection)
      window.removeEventListener('resize', updateActiveSection)
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [composerMode])
  useEffect(() => {
    if (!assetSearch) {
      setDebouncedAssetSearch('')
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedAssetSearch(assetSearch)
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [assetSearch])

  // Baseline set of offerings auto-selected by the offering picker on a pristine
  // form (all enabled books) — excluded from the dirty check so the default
  // selection alone doesn't arm the unsaved-changes guard.
  const [defaultTargetOfferingsBaseline, setDefaultTargetOfferingsBaseline] = useState([])

  const draftKey = useMemo(
    () => buildDraftKey(currentOrg?.slug, mode, postIdParam),
    [currentOrg?.slug, mode, postIdParam]
  )
  const uiPrefsKey = useMemo(() => buildUiPrefsKey(currentOrg?.slug), [currentOrg?.slug])

  const { data: providerInfo } = useQuery({
    queryKey: ['provider-info', currentOrg?.slug],
    queryFn: async () => {
      const res = await api.get(`/${currentOrg.slug}/provider-info`)
      return res.data
    },
    enabled: !!currentOrg?.slug,
  })
  const providerAdapter = providerInfo?.provider?.ingest_adapter || ''
  const providerName = providerInfo?.provider?.name || ''
  const isKambiOrg = providerAdapter === 'kambi' || providerName.startsWith?.('kambi') || false
  const isCryptoDotComOrg = providerAdapter === 'cryptodotcom' || providerName === 'Crypto.com'
  const isOGOrg = providerAdapter === 'og' || providerName === 'OG'
  const authoredWagerValidationError = isKambiOrg
    ? validateAuthoredWagerEntries(formData.wagerEntries, formData.kambiOutcomeIds, kambiValidation?.outcomes)
    : ''
  const marketValidationError = isKambiOrg && formData.kambiOutcomeIds.length > 0 && !kambiValidation
    ? 'Wait for provider validation to finish, or change the selected market.'
    : authoredWagerValidationError
  const isPredictionContractOrg = isCryptoDotComOrg || isOGOrg
  const providerKind = getPostComposerProviderKind({
    isKambi: isKambiOrg,
    isCryptoDotCom: isCryptoDotComOrg,
    isOG: isOGOrg,
  })

  // Dropping a regulation from the post must drop any caption written for it,
  // otherwise submit would fork a regulation the post no longer targets.
  useEffect(() => {
    const allowed = new Set(formData.targetOfferings || [])
    setRegulationCopy((current) => {
      const kept = Object.entries(current).filter(([offeringId]) => allowed.has(offeringId))
      return kept.length === Object.keys(current).length ? current : Object.fromEntries(kept)
    })
  }, [formData.targetOfferings])
  const hasBetDisclosureAttention = Boolean(
    marketSearchQuery.trim()
    || formData.selectionIds.length
    || formData.kambiOutcomeIds.length
    || formData.providerSelectionIds.length
    || formData.wagerEntries.length
    || marketValidationError
    || workflowError?.step === 'market'
  )

  useEffect(() => {
    if (hasBetDisclosureAttention) setOnePageBetExpanded(true)
  }, [
    formData.kambiOutcomeIds,
    formData.providerSelectionIds,
    formData.selectionIds,
    formData.wagerEntries,
    hasBetDisclosureAttention,
    marketSearchQuery,
    marketValidationError,
    workflowError,
  ])


  // One cache key for the org's offerings, shared with KambiOutcomeSelector so
  // relocating the regulation picker does not add another request.
  const { data: kambiOfferingsData } = useQuery({
    queryKey: ['kambi-offerings', currentOrg?.slug],
    queryFn: () => kambiApi.getOfferings(currentOrg.slug),
    enabled: !!currentOrg?.slug && isKambiOrg,
  })

  const { data: tagsData } = useQuery({
    queryKey: ['tags', currentOrg?.slug],
    queryFn: () => tagApi.list(currentOrg.slug, true),
    enabled: !!currentOrg?.slug,
  })


  const allTags = useMemo(() => tagsData?.tags || [], [tagsData?.tags])

  const { data: brandingData } = useQuery({
    queryKey: ['post-creator-branding', currentOrg?.id],
    queryFn: () => organizationApi.getBranding(currentOrg.id),
    enabled: !!currentOrg?.id,
  })

  const serverDraftQuery = useQuery({
    queryKey: ['post-creator-draft', currentOrg?.slug],
    queryFn: () => postApi.getDraft(currentOrg.slug),
    enabled: !!currentOrg?.slug && !isEditMode,
    retry: false,
  })

  const {
    data: libraryAssetsData,
    fetchNextPage: fetchNextLibraryAssetsPage,
    hasNextPage: hasNextLibraryAssetsPage,
    isFetchingNextPage: isFetchingNextLibraryAssetsPage,
    isFetchNextPageError: isFetchNextLibraryAssetsPageError,
    isError: isLibraryAssetsError,
  } = useInfiniteQuery({
    queryKey: ['post-creator-library-assets', currentOrg?.slug, debouncedAssetSearch],
    queryFn: ({ pageParam = null }) => mediaLibraryApi.listAssets(currentOrg.slug, {
      source: 'library',
      ...(pageParam ? { cursor: pageParam } : {}),
      include_associations: false,
      ...(debouncedAssetSearch ? { search: debouncedAssetSearch } : {}),
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: !!currentOrg?.slug && mediaActivePanel === 'library',
  })

  const { data: promptTemplatesData } = useQuery({
    queryKey: ['post-creator-ai-prompt-presets', currentOrg?.slug],
    queryFn: () => mediaLibraryApi.listTemplates(currentOrg.slug, { template_kind: AI_PROMPT_TEMPLATE_KIND }),
    enabled: !!currentOrg?.slug && mediaActivePanel === 'generate',
    staleTime: 60_000,
  })

  const aiCapabilitiesQuery = useQuery({
    queryKey: ['post-creator-ai-capabilities', currentOrg?.slug],
    queryFn: () => aiApi.getCapabilities(currentOrg.slug),
    enabled: !!currentOrg?.slug,
  })
  const aiCapabilities = aiCapabilitiesQuery.data || {}
  const pollingTranscriptionEntries = useMemo(
    () => Object.entries(transcriptionJobs).filter(([, entry]) => (
      (entry?.status === 'polling' || entry?.status === 'retrying')
      && entry.jobId
      && entry.orgSlug
      && entry.orgSlug === currentOrg?.slug
    )),
    [currentOrg?.slug, transcriptionJobs]
  )
  const transcriptionJobQueries = useQueries({
    queries: pollingTranscriptionEntries.map(([, entry]) => ({
      queryKey: ['ai-transcription-job', entry.orgSlug, entry.jobId],
      queryFn: async () => {
        // Fetch before enforcing the deadline: a job that completed while the
        // tab was backgrounded must land its results, not report a timeout.
        const response = await aiApi.getJob(entry.orgSlug, entry.jobId)
        const status = response?.job?.status
        const stillRunning = !status || status === 'pending' || status === 'processing'
        if (stillRunning && Date.now() - (entry.startedAt || Date.now()) > TRANSCRIPTION_TIMEOUT_MS) {
          const error = new Error('Transcription timed out')
          error.isTranscriptionTimeout = true
          throw error
        }
        return response
      },
      refetchInterval: (query) => {
        // The queryFn's timeout throw is the only deadline authority; stopping
        // the interval on elapsed time here would race it and strand the entry
        // in 'polling' forever.
        if (query.state.status === 'error') {
          return false
        }

        const status = query.state.data?.job?.status
        if (!status || status === 'pending' || status === 'processing') {
          return 3000
        }
        return false
      },
      retry: false,
    })),
  })
  // Suggested bets search what the market search searches (searchScope below).
  const suggestedBetsOfferingsRef = useRef(EMPTY_OFFERINGS)
  const {
    requestSuggestedBets,
    suggestBetsMutation,
  } = useSuggestedBets({
    currentOrg,
    setAiReview,
    searchOfferingsRef: suggestedBetsOfferingsRef,
  })
  useTranscriptionJobPolling({
    pollingTranscriptionEntries,
    transcriptionJobQueries,
    setAiReview,
    setTranscriptionJobs,
  })
  const libraryAssets = useMemo(() => {
    return libraryAssetsData?.pages?.flatMap((page) => page?.items || []) || []
  }, [libraryAssetsData])
  const imagePromptPresets = useMemo(
    () => (promptTemplatesData?.items || []).filter((template) => template.template_kind === AI_PROMPT_TEMPLATE_KIND && template.is_active !== false),
    [promptTemplatesData?.items],
  )
  const selectedPromptPreset = useMemo(
    () => imagePromptPresets.find((template) => template.id === selectedPromptPresetId) || null,
    [imagePromptPresets, selectedPromptPresetId],
  )

  useEffect(() => {
    if (defaultPromptPresetAppliedRef.current || !imagePromptPresets.length) return
    const defaultPreset = imagePromptPresets.find((preset) => preset.defaults?.ai_image_prompt?.is_default) || null
    if (!defaultPreset) return

    setSelectedPromptPresetId(defaultPreset.id)
    setImagePromptFields(aiPromptFieldsFromTemplate(defaultPreset))
    setImagePrompt(aiPromptTextFromTemplate(defaultPreset))
    defaultPromptPresetAppliedRef.current = true
  }, [imagePromptPresets, setImagePrompt, setImagePromptFields, setSelectedPromptPresetId])

  useEffect(() => {
    const prefs = parseStoredJson(uiPrefsKey, {})
    const previouslyExpanded = prefs.advancedSectionExpanded
    const legacyExpanded = Object.values(prefs.advancedSections || {}).some(Boolean)
    setAdvancedSectionExpanded(
      typeof previouslyExpanded === 'boolean'
        ? previouslyExpanded
        : legacyExpanded
    )
    if (!isEditMode) {
      setComposerMode(prefs.composerMode === 'guided' ? 'guided' : 'one-page')
    }
    setUiPrefsReadyKey(uiPrefsKey)
  }, [isEditMode, uiPrefsKey])

  useEffect(() => {
    if (uiPrefsReadyKey !== uiPrefsKey) return
    const rememberedMode = isEditMode ? parseStoredJson(uiPrefsKey, {}).composerMode : composerMode
    persistStoredJson(uiPrefsKey, {
      advancedSectionExpanded,
      composerMode: rememberedMode === 'guided' ? 'guided' : 'one-page',
    })
  }, [advancedSectionExpanded, composerMode, isEditMode, uiPrefsKey, uiPrefsReadyKey])

  const previewLegs = useMemo(() => {
    if (isPredictionContractOrg && providerSelectionDetails.length > 0) {
      return providerSelectionDetails.map((selection) => buildProviderContractPreviewLeg(selection, isOGOrg ? 'og' : 'cryptodotcom'))
    }

    if (!kambiValidation?.outcomes || !formData.kambiOutcomeIds.length) return []

    const eventsByName = {}
    for (const evt of (kambiValidation.events || [])) {
      eventsByName[evt.name] = evt
    }

    return formData.kambiOutcomeIds
      .map((id) => {
        const data = kambiValidation.outcomes[String(id)]
        if (!data) return null
        const evt = eventsByName[data.event_name]
        return kambiOutcomePreviewLeg(data, evt)
      })
      .filter(Boolean)
  }, [isPredictionContractOrg, isOGOrg, providerSelectionDetails, kambiValidation, formData.kambiOutcomeIds])

  const previewPayload = useMemo(() => {
    const selectedTagObjects = allTags.filter((tag) => formData.tagIds.includes(tag.id))
    return normalizeFeedPreviewItem({
      payload: {
        post_id: 'preview',
        text: formData.text || '',
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        user: selectedCreator
          ? { display_name: selectedCreator.display_name, avatar_url: selectedCreator.avatar_url }
          : editCreator || { display_name: 'Creator Name' },
        selection_set: isKambiOrg
          ? buildAuthoredWagerPreviewSelectionSet(formData.wagerEntries, previewLegs)
          : undefined,
        media_objects: uploadedMedia
          .map(normalizePostPreviewMedia)
          .filter(Boolean),
        legs: previewLegs,
        card_presentation: formData.cardPresentation || undefined,
        cta: serializePostComposerCTA(formData),
        tags: selectedTagObjects.map((tag) => ({
          label: tag.label,
          bg_color: tag.bg_color,
          text_color: tag.text_color,
          icon: tag.icon,
          font_style: tag.font_style,
          position: tag.position,
        })),
        post_type: formData.postType,
        expires_at: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
        promo_priority: formData.promoPriority,
        interleave_every: formData.placementMode === 'interleave' && formData.interleaveEvery ? parseInt(formData.interleaveEvery, 10) : null,
        created_at: new Date().toISOString(),
        share_enabled: formData.shareEnabled !== false,
        copy_count: resolvePostComposerCopyCountEnabled(formData) ? 23 : undefined,
      },
    })
  }, [allTags, editCreator, formData, isKambiOrg, previewLegs, selectedCreator, uploadedMedia])

  const creatorOfferingRestrictions = useMemo(() => {
    const allowedOfferings = selectedCreator?.allowed_offerings || []
    const availableOfferings = kambiOfferingsData?.offering_ids || []
    if (!allowedOfferings.length || !availableOfferings.length) {
      return { disabledIds: [], reasonById: {} }
    }

    const disabledIds = availableOfferings.filter((offeringId) => !allowedOfferings.includes(offeringId))
    const reasonById = Object.fromEntries(
      disabledIds.map((offeringId) => [offeringId, 'Restricted for this creator'])
    )

    return { disabledIds, reasonById }
  }, [kambiOfferingsData?.offering_ids, selectedCreator?.allowed_offerings])

  useEffect(() => {
    if (isEditMode || !creatorOfferingRestrictions.disabledIds.length) {
      return
    }

    setFormData((currentValue) => {
      const filteredOfferings = currentValue.targetOfferings.filter((offeringId) => !creatorOfferingRestrictions.disabledIds.includes(offeringId))
      if (filteredOfferings.length === currentValue.targetOfferings.length) {
        return currentValue
      }
      return { ...currentValue, targetOfferings: filteredOfferings }
    })
  }, [creatorOfferingRestrictions.disabledIds, isEditMode, setFormData])

  const regulationLookup = useMemo(
    () => buildRegulationLookup(kambiOfferingsData?.mappings || []),
    [kambiOfferingsData?.mappings],
  )

  // Every regulation the org can target, with creator restrictions marked
  // rather than hidden, so an operator can see why one is unavailable.
  const regulationTargets = useMemo(() => {
    if (!isKambiOrg) return []
    return (kambiOfferingsData?.offering_ids || []).map((offeringId) => {
      const isRestricted = creatorOfferingRestrictions.disabledIds.includes(offeringId)
      const isStoredTarget = isEditMode && formData.targetOfferings.includes(offeringId)
      return {
        value: offeringId,
        label: regulationDisplayLabel(offeringId, regulationLookup(offeringId)),
        disabled: isRestricted && !isStoredTarget,
      }
    })
  }, [creatorOfferingRestrictions.disabledIds, formData.targetOfferings, isEditMode, isKambiOrg, kambiOfferingsData?.offering_ids, regulationLookup])

  const enabledOfferings = useMemo(
    () => regulationTargets.filter((target) => !target.disabled).map((target) => target.value),
    [regulationTargets],
  )

  const regulationLabel = useCallback(
    (offeringId) => regulationDisplayLabel(offeringId, regulationLookup(offeringId)),
    [regulationLookup],
  )

  // Availability is a property of the market, not the selection, so notices
  // survive edits to Where it runs and clear only when the market changes.
  const regulationNotices = useMemo(() => (
    regulationAvailability && regulationAvailability.kambiOutcomeIds === formData.kambiOutcomeIds
      ? regulationNoticesByOfferingId(regulationAvailability)
      : {}
  ), [formData.kambiOutcomeIds, regulationAvailability])

  // Regulation-specific copy only covers regulations the post actually targets.
  const regulationChoices = useMemo(() => {
    if (!isKambiOrg) return []
    return (formData.targetOfferings || []).map((offeringId) => ({
      value: offeringId,
      label: regulationDisplayLabel(offeringId, regulationLookup(offeringId)),
    }))
  }, [formData.targetOfferings, isKambiOrg, regulationLookup])

  const searchScope = useMemo(() => marketSearchScope({
    targetOfferings: formData.targetOfferings || EMPTY_OFFERINGS,
    offeringIds: kambiOfferingsData?.offering_ids || EMPTY_OFFERINGS,
    defaultOfferingId: kambiOfferingsData?.default_offering_id || '',
    scope: marketSearchScopeChoice,
    labelFor: regulationLabel,
  }), [formData.targetOfferings, kambiOfferingsData?.default_offering_id, kambiOfferingsData?.offering_ids, marketSearchScopeChoice, regulationLabel])
  suggestedBetsOfferingsRef.current = searchScope.offerings

  const setTargetOfferings = useCallback((offerings, meta) => {
    setFormData((prev) => ({ ...prev, targetOfferings: offerings }))
    if (meta?.isDefaultSelection) setDefaultTargetOfferingsBaseline(offerings)
  }, [setFormData])

  // The picker used to live inside the optional bet disclosure, so a new post
  // that never opened it kept an empty regulation set forever. Defaulting here
  // runs regardless of which step is mounted.
  //
  // Never in edit mode. An empty target_offerings means "every regulation,
  // including ones added later" (see the feed-api serving predicate), so
  // auto-filling a saved post would silently freeze it to today's list — and
  // it would race the async post load, which can land an empty set first.
  const offeringsInitializedRef = useRef(false)
  useEffect(() => {
    if (isEditMode || offeringsInitializedRef.current || enabledOfferings.length === 0) return
    offeringsInitializedRef.current = true
    if ((formData.targetOfferings || []).length === 0) {
      setTargetOfferings(enabledOfferings, { isDefaultSelection: true })
    }
  }, [enabledOfferings, formData.targetOfferings, isEditMode, setTargetOfferings])

  const handleToggleRegulation = useCallback((offeringId) => {
    setFormData((prev) => {
      const current = prev.targetOfferings || []
      return {
        ...prev,
        targetOfferings: current.includes(offeringId)
          ? current.filter((id) => id !== offeringId)
          : [...current, offeringId],
      }
    })
  }, [setFormData])

  useEffect(() => {
    if (!isEditMode || !currentOrg?.slug) return

    editBaselineRef.current = null
    const fetchPost = async () => {
      setLoadError(null)
      try {
        const post = await postApi.get(currentOrg.slug, postIdParam)
        // The moderation panel reads this same response under its own key:
        // seeded, it mounts from this load instead of a second GET, and polls on from it.
        queryClient.setQueryData(['post-moderation', currentOrg.slug, postIdParam], post)
        const loadedFormData = {
          userId: post.creator_id || post.content_user_id || '',
          text: post.text || '',
          title: post.title || '',
          subtitle: post.subtitle || '',
          selectionIds: (post.bet_legs || []).map((leg) => leg.selection_id),
          mediaObjects: (post.media_objects || []).map((media) => ({
            id: media.id,
            type: media.type,
            url: media.url,
            object_key: media.object_key,
            source_object_key: media.source_object_key,
            ...(media.type === 'video'
              ? { captions: serializeCaptionTracks(normalizeCaptionTracks(media.metadata?.captions)) }
              : {}),
          })),
          ctaKind: post.cta?.kind || 'betslip',
          ctaLabel: post.cta?.label || '',
          ctaTarget: post.cta?.target || '',
          score: post.score ?? 1.0,
          isHidden: post.is_hidden || false,
          isPinned: post.is_pinned || false,
          pinnedUntil: post.pinned_until ? new Date(post.pinned_until).toISOString().slice(0, 16) : '',
          boostFactor: post.boost_factor ?? 1.0,
          surfaceIds: Array.isArray(post.surface_targets)
            ? post.surface_targets.map((target) => target.id)
            : [],
          skipApproval: false,
          kambiOutcomeIds: (post.bet_legs || [])
            .map((leg) => parseNumericOutcomeId(leg.external_id))
            .filter((value) => value !== null),
          wagerEntries: reconcileAuthoredWagerEntries(
            post.wager_entries || [],
            (post.bet_legs || [])
              .map((leg) => parseNumericOutcomeId(leg.external_id))
              .filter((value) => value !== null),
          ),
          providerSelectionIds: (post.bet_legs || [])
            .map((leg) => leg.external_id)
            .filter((value) => value && !isNumericExternalId(value)),
          targetOfferings: post.target_offerings || [],
          tagIds: (post.tags || []).map((tag) => tag.id),
          postType: post.post_type || 'organic',
          expiresAt: formatDateTimeInput(post.expires_at),
          archiveOnCompletion: Boolean(post.archive_on_completion),
          scheduledFor: formatDateTimeInput(post.scheduled_for),
          promoPriority: post.promo_priority ?? 0,
          interleaveEvery: post.interleave_every ? String(post.interleave_every) : '',
          placementMode: post.interleave_every ? 'interleave' : 'pinned',
          shareEnabled: post.share_enabled !== false,
          copyCountEnabled: Boolean(post.copy_count_enabled),
          cardPresentation: normalizeCardPresentation(post.card_presentation),
        }
        initialEditFormDataRef.current = loadedFormData
        setFormData(loadedFormData)
        setEditCreator(post.creator?.name
          ? { display_name: post.creator.name, avatar_url: post.creator.avatar_url || '' }
          : null)

        setUploadedMedia(
          createUploadedMediaFromStoredMedia(
            (post.media_objects || []).map((media) => ({
              id: media.id || crypto.randomUUID(),
              type: media.type,
              name: media.url.split('/').pop() || 'media',
              mediaId: media.id,
              previewUrl: media.url,
              url: media.url,
              object_key: media.object_key,
              source_object_key: media.source_object_key,
              captions: media.metadata?.captions,
            }))
          )
        )
      } catch (error) {
        setLoadError(error?.response?.status === 404
          ? { kind: 'not_found' }
          : { kind: 'failed', message: extractErrorMessage(error) })
      } finally {
        setLoading(false)
      }
    }

    fetchPost()
  }, [currentOrg?.slug, isEditMode, loadAttempt, postIdParam, queryClient, setFormData])

  useEffect(() => {
    if (!draftKey) return
    if (isEditMode && loading) return

    const localDraft = parseStoredJson(draftKey)

    if (isEditMode) {
      // An edit page offers only its own post's draft, and only one that
      // differs from the post as loaded. The server keeps one create draft
      // per user and organization, so it is never offered here.
      editBaselineRef.current = editDraftSignature(latestDraftInputRef.current.formData, latestDraftInputRef.current.uploadedMedia)
      const changedDraft = localDraft && editDraftSignature(localDraft.formData, localDraft.uploadedMedia) !== editBaselineRef.current
      setPendingDraft(changedDraft ? localDraft : null)
      setDraftReady(true)
      return
    }

    const serverDraft = serverDraftQuery.data?.draft || null
    setPendingDraft(selectNewestDraft(localDraft, serverDraft))

    setDraftReady(Boolean(serverDraftQuery.isFetched || serverDraftQuery.isError || !currentOrg?.slug))
  }, [currentOrg?.slug, draftKey, isEditMode, loading, serverDraftQuery.data?.draft, serverDraftQuery.isError, serverDraftQuery.isFetched])

  const restoreDraft = (surfaceIdsOverride) => {
    if (!pendingDraft) return

    const restoredMedia = createUploadedMediaFromStoredMedia(pendingDraft.uploadedMedia)
    const restoredFormData = restoreDraftFormData(pendingDraft.formData)
    if (Array.isArray(surfaceIdsOverride)) {
      restoredFormData.surfaceIds = surfaceIdsOverride
    }
    const draftAdjunct = pendingDraft.adjunct || pendingDraft
    setFormData(restoredFormData)
    setUploadedMedia(restoredMedia)
    setSelectedCreator(normalizeSelectedCreator(pendingDraft.selectedCreator))
    setAiReview(normalizeComposerAiReview(draftAdjunct.aiReview))
    setTranscriptionJobs(normalizeDraftTranscriptionJobs(draftAdjunct.transcriptionJobs, restoredMedia))
    setImagePrompt(typeof draftAdjunct.imagePrompt === 'string' ? draftAdjunct.imagePrompt : '')
    setImagePromptFields(normalizeAiPromptFields(draftAdjunct.imagePromptFields || {}))
    setSelectedPromptPresetId(draftAdjunct.selectedPromptPresetId || '')
    setReferenceImages(normalizeComposerReferenceImages(draftAdjunct.referenceImages))
    setRevisionPrompt(typeof draftAdjunct.revisionPrompt === 'string' ? draftAdjunct.revisionPrompt : '')
    setGeneratedAssetIds(Array.isArray(draftAdjunct.generatedAssetIds) ? draftAdjunct.generatedAssetIds : [])
    if (draftAdjunct.generatedImageReview) {
      setGeneratedImageReview(draftAdjunct.generatedImageReview)
    }
    if (draftAdjunct.imageGenerationJob) {
      setImageGenerationJob(restoreImageGenerationJob(draftAdjunct.imageGenerationJob))
    }
    setPendingDraft(null)
    toast.success(
      Array.isArray(surfaceIdsOverride)
        ? `Draft restored to ${launchSurfaceLabel}`
        : 'Draft restored',
    )
  }

  const discardDraft = () => {
    removeStoredValue(draftKey)
    if (currentOrg?.slug && !isEditMode) {
      postApi.deleteDraft(currentOrg.slug).catch(() => {})
      // The cached copy would be offered again on the next visit.
      queryClient.setQueryData(['post-creator-draft', currentOrg.slug], { draft: null })
    }
    setPendingDraft(null)
  }

  const handleToggleChange = (field, value) => {
    setFormData((previousValue) => ({
      ...previousValue,
      [field]: value,
    }))
  }

  const handleSelectionsChange = (selections) => {
    setFormData((previousValue) => ({
      ...previousValue,
      selectionIds: selections,
    }))
  }

  const handleProviderSelectionIdsChange = (ids, selections = providerSelectionDetails) => {
    setProviderSelectionDetails(selections)
    setFormData((previousValue) => {
      const nextValue = {
        ...previousValue,
        providerSelectionIds: ids,
      }

      if (!isPredictionContractOrg || (previousValue.ctaKind !== 'betslip' && previousValue.ctaKind !== 'provider-cart')) {
        return nextValue
      }

      if (ids.length === 0) {
        return {
          ...nextValue,
          ctaKind: 'betslip',
          ctaLabel: '',
          ctaTarget: '',
        }
      }

      const first = selections[0]
      const meta = first?.selection_metadata || {}
      return {
        ...nextValue,
        ctaKind: 'provider-cart',
        ctaLabel: ids.length === 1 ? `${isOGOrg ? 'Trade' : 'Buy'} ${isOGOrg ? providerContractLabel(first) : cryptoSelectionLabel(first)}` : 'Trade',
        ctaTarget: meta.detail_url || previousValue.ctaTarget || '',
      }
    })
  }

  const showWorkflowError = useCallback((issue) => {
    setWorkflowError(issue)
  }, [])

  useEffect(() => {
    if (!workflowError) return undefined
    const focusFrame = window.requestAnimationFrame(() => workflowErrorRef.current?.focus())
    return () => window.cancelAnimationFrame(focusFrame)
  }, [activeStep, workflowError])

  const changeStep = useCallback((step) => {
    setWorkflowError(null)
    setActiveStep(step)
    window.requestAnimationFrame(() => {
      document.getElementById(`post-composer-${step}-heading`)?.focus()
    })
  }, [])
  const changeComposerMode = useCallback((nextMode) => {
    setComposerMode(nextMode)
    if (nextMode === 'guided') {
      setActiveStep('content')
    }
  }, [])


  const captureMarketValidator = useCallback((validator) => {
    // The market editor unmounts outside the Bet step in Guided mode. Keep the
    // latest validated provider boundary for the final publish check.
    if (validator?.revalidate) marketValidationRef.current = validator
  }, [])

  const handleKambiValidationResult = useCallback((result) => {
    setKambiValidation(result)
    if (!result?.outcomes) return
    setFormData((previousValue) => {
      const wagerEntries = applyValidatedDefaultWagerType(previousValue.wagerEntries, result.outcomes)
      return wagerEntries === previousValue.wagerEntries
        ? previousValue
        : { ...previousValue, wagerEntries }
    })
  }, [setFormData])

  const handleSelectKambiResult = useCallback((result) => {
    const numericOutcomeId = parseOutcomeId(result?.leg?.outcome_id)
    if (numericOutcomeId === null) {
      showWorkflowError({
        step: 'market',
        message: 'This search result cannot be passed safely to provider validation. Choose another market or enter a valid outcome ID.',
      })
      return
    }

    setKambiValidation(null)
    setFormData((previousValue) => {
      const nextOutcomeIds = previousValue.kambiOutcomeIds.includes(numericOutcomeId)
        ? previousValue.kambiOutcomeIds
        : [...previousValue.kambiOutcomeIds, numericOutcomeId]
      return {
        ...previousValue,
        kambiOutcomeIds: nextOutcomeIds,
        wagerEntries: reconcileAuthoredWagerEntries(previousValue.wagerEntries, nextOutcomeIds, { preferParlay: true }),
      }
    })
  }, [setFormData, setKambiValidation, showWorkflowError])

  const handleContinue = () => {
    const issue = validatePostComposerStep(activeStep, formData, {
      hasEnabledSurfaces,
      marketValidationError,
    })
    if (issue) {
      showWorkflowError(issue)
      return
    }

    const currentIndex = POST_COMPOSER_SECTION_REGISTRY.findIndex((entry) => entry.id === activeStep)
    const nextStep = POST_COMPOSER_SECTION_REGISTRY[currentIndex + 1]
    if (nextStep) changeStep(nextStep.id)
  }

  const handleBack = () => {
    const currentIndex = POST_COMPOSER_SECTION_REGISTRY.findIndex((entry) => entry.id === activeStep)
    const previousStep = POST_COMPOSER_SECTION_REGISTRY[currentIndex - 1]
    if (previousStep) changeStep(previousStep.id)
  }

  const clearDraft = useCallback(() => {
    const hadLocalDraft = Boolean(parseStoredJson(draftKey))
    removeStoredValue(draftKey)
    if ((hadLocalDraft || serverDraftQuery.data?.draft) && currentOrg?.slug && !isEditMode) {
      postApi.deleteDraft(currentOrg.slug).catch(() => {})
      // Drop the cached copy too, so the next Create post does not offer the
      // cleared draft while its refetch is in flight.
      queryClient.setQueryData(['post-creator-draft', currentOrg.slug], { draft: null })
    }
  }, [currentOrg?.slug, draftKey, isEditMode, queryClient, serverDraftQuery.data?.draft])

  const hasInProgressContent = useMemo(
    () => hasComposerContent(
      formData,
      uploadedMedia,
      selectedCreator,
      false,
      defaultTargetOfferingsBaseline,
      launchSurfaceIdsBaseline,
      adjunct,
    ),
    [
      adjunct,
      defaultTargetOfferingsBaseline,
      formData,
      launchSurfaceIdsBaseline,
      selectedCreator,
      uploadedMedia,
    ],
  )

  useEffect(() => {
    if (!draftKey || !draftReady || pendingDraft || isSubmitting || submissionCompletedRef.current) return

    if (!hasInProgressContent) {
      clearDraft()
      return
    }
    // An edit page left as loaded stores nothing.
    if (isEditMode && editBaselineRef.current !== null && editDraftSignature(formData, uploadedMedia) === editBaselineRef.current) {
      removeStoredValue(draftKey)
      return
    }

    const draftPayload = {
      version: 1,
      savedAt: new Date().toISOString(),
      formData,
      uploadedMedia: uploadedMedia.map(draftMediaEntry),
      selectedCreator: normalizeSelectedCreator(selectedCreator),
      adjunct: {
        aiReview: {
          ...normalizeComposerAiReview(aiReview),
          transcript: String(aiReview.transcript || '').slice(0, 20000),
        },
        transcriptionJobs: Object.entries(transcriptionJobs)
          .filter(([mediaId]) => uploadedMedia.some((media) => media.id === mediaId))
          .map(([mediaId, entry]) => ({
            mediaId,
            jobId: entry.jobId || null,
            orgSlug: entry.orgSlug || null,
            status: entry.status,
            error: entry.error || '',
            errorCode: entry.errorCode || null,
            retryable: entry.retryable === true,
            attempt: entry.attempt || 1,
            retryOf: entry.retryOf || null,
            mediaUrl: entry.mediaUrl || null,
            title: entry.title || null,
            contentType: entry.contentType || null,
            startedAt: entry.startedAt,
            completedAt: entry.completedAt || null,
          })),
        imageGenerationJob,
        imagePrompt,
        imagePromptFields,
        selectedPromptPresetId,
        referenceImages,
        generatedImageReview,
        revisionPrompt,
        generatedAssetIds,
      },
    }

    persistStoredJson(draftKey, {
      ...draftPayload,
    })
    const timeoutId = window.setTimeout(() => {
      if (currentOrg?.slug && !isEditMode) {
        postApi.saveDraft(currentOrg.slug, draftPayload).catch(() => {})
      }
    }, 700)

    return () => window.clearTimeout(timeoutId)
  }, [
    currentOrg?.slug,
    clearDraft,
    draftKey,
    draftReady,
    formData,
    generatedAssetIds,
    generatedImageReview,
    hasInProgressContent,
    imageGenerationJob,
    imagePrompt,
    imagePromptFields,
    isEditMode,
    isSubmitting,
    pendingDraft,
    referenceImages,
    revisionPrompt,
    selectedCreator,
    selectedPromptPresetId,
    aiReview,
    transcriptionJobs,
    uploadedMedia,
  ])

  useClientWorkGuard(uploadedMedia.some((media) => media.uploading || media.captionUploading))

  useBlobPreviewUrlCleanup(uploadedMedia)

  useEffect(() => {
    const templatePrefill = location.state?.prefills?.template
    const shouldBypassDraft = location.state?.prefills?.source === 'assets'

    if (isEditMode || !draftReady || mediaLibraryPrefillAppliedRef.current || (pendingDraft && !shouldBypassDraft)) {
      return
    }

    if (!templatePrefill) {
      return
    }

    const templateAttachments = (templatePrefill.assets || [])
      .filter((asset) => asset?.url)
      .map((asset) => createMediaAttachment(asset))
    const templateAssets = templateAttachments.map((attachment) => attachment.uploadedMedia)
    const templateMediaObjects = templateAttachments.map((attachment) => attachment.mediaObject)

    const defaults = templatePrefill.defaults || {}

    setUploadedMedia((currentValue) => {
      const knownAssetIds = new Set(currentValue.map((item) => item.mediaId).filter(Boolean))
      const knownUrls = new Set(currentValue.map((item) => item.url).filter(Boolean))
      return [...currentValue, ...templateAssets.filter((item) => !knownAssetIds.has(item.mediaId) && !knownUrls.has(item.url))]
    })

    setFormData((currentValue) => ({
      ...currentValue,
      text: currentValue.text || defaults.text || '',
      title: currentValue.title || defaults.title || '',
      subtitle: currentValue.subtitle || defaults.subtitle || '',
      ctaKind: defaults.cta_kind || currentValue.ctaKind,
      ctaLabel: currentValue.ctaLabel || defaults.cta_label || '',
      ctaTarget: currentValue.ctaTarget || defaults.cta_target || '',
      tagIds: currentValue.tagIds.length ? currentValue.tagIds : (defaults.tag_ids || []),
      mediaObjects: [
        ...currentValue.mediaObjects,
        ...templateMediaObjects.filter((asset) => !currentValue.mediaObjects.some((item) => item.mediaId === asset.mediaId || item.url === asset.url)),
      ],
    }))

    if (typeof defaults.is_hidden === 'boolean' || typeof defaults.is_pinned === 'boolean') {
      setFormData((currentValue) => ({
        ...currentValue,
        isHidden: typeof defaults.is_hidden === 'boolean' ? defaults.is_hidden : currentValue.isHidden,
        isPinned: typeof defaults.is_pinned === 'boolean' ? defaults.is_pinned : currentValue.isPinned,
      }))
    }

    mediaLibraryPrefillAppliedRef.current = true
    toast.success(shouldBypassDraft ? 'Media attached to post' : `Loaded template: ${templatePrefill.name || 'Media template'}`)
  }, [draftReady, isEditMode, location.state, pendingDraft, setFormData])

  const attachAssetToComposer = (asset) => {
    if (!asset?.url || assetAttachBlocked(asset)) return
    attachAssetToState(asset, { setUploadedMedia, setFormData })
    toast.success(`Attached asset: ${asset.title}`)
  }

  const suggestCaptionMutation = useMutation({
    mutationFn: (directionOverride) => aiApi.suggestCaption(currentOrg.slug, {
      text: formData.text || '',
      direction: String(directionOverride || captionGuidance).trim() || undefined,
      count: 3,
    }),
    onSuccess: (response) => {
      const parsed = parseAiJson(response.response)
      const captions = [
        ...(Array.isArray(response?.captions) ? response.captions : []),
        ...(Array.isArray(parsed?.captions) ? parsed.captions : []),
        parsed?.caption,
        response?.caption,
      ].filter((caption, index, values) => caption && values.indexOf(caption) === index)
      setAiReview((currentValue) => ({ ...currentValue, caption: captions[0] || '', captions: captions.slice(0, 3) }))
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to suggest caption'),
  })

  const attachedImageUrls = useMemo(
    () => (formData.mediaObjects || []).filter((media) => isImageMedia(media) && media?.url).map((media) => media.url).slice(0, MAX_SUGGESTION_IMAGES),
    [formData.mediaObjects],
  )
  const hasAnalyzableContent = useMemo(
    () => Boolean(formData.text.trim() || aiReview.transcript || aiReview.betSummary || attachedImageUrls.length),
    [aiReview.betSummary, aiReview.transcript, attachedImageUrls, formData.text],
  )
  const findBetsInputsRef = useRef(null)
  findBetsInputsRef.current = {
    hasContent: hasAnalyzableContent,
    text: formData.text,
    transcript: aiReview.transcript,
    betSummary: aiReview.betSummary,
    imageUrls: attachedImageUrls,
  }
  const findSuggestedBets = useCallback(() => {
    const { hasContent, text, transcript, betSummary, imageUrls } = findBetsInputsRef.current
    if (!hasContent) return
    requestSuggestedBets({
      text: text || undefined,
      transcript: transcript || undefined,
      summary: betSummary || undefined,
      image_urls: imageUrls.length ? imageUrls : undefined,
      __source: transcript ? (imageUrls.length ? 'video+image' : 'video') : imageUrls.length ? 'image' : 'text',
    })
  }, [requestSuggestedBets])
  const suggestBetsStatus = suggestBetsMutation.isPending ? 'loading' : suggestBetsMutation.isError ? 'error' : 'idle'
  const suggestedBetsProps = useMemo(() => {
    // Wager-only until the feature has proven itself (#870); the API refuses client roles too.
    if (!(isInternalRole && isKambiOrg && aiCapabilities.features?.bet_suggestions?.enabled)) return null
    return {
      suggestions: aiReview.suggestedBets,
      unmatched: aiReview.suggestedBetsUnmatched,
      warnings: aiReview.suggestedBetsWarnings,
      source: aiReview.suggestedBetsSource,
      status: suggestBetsStatus,
      attachedOutcomeIds: formData.kambiOutcomeIds,
      onAttach: (suggestion) => handleSelectKambiResult({ leg: { outcome_id: suggestion.result?.selection_ref?.external_outcome_id } }),
      onDismiss: (suggestion) => setAiReview((currentValue) => ({
        ...currentValue,
        suggestedBets: currentValue.suggestedBets.filter((item) => String(item?.result?.selection_ref?.external_outcome_id) !== String(suggestion?.result?.selection_ref?.external_outcome_id)),
      })),
      onFindBets: findSuggestedBets,
      findDisabledReason: hasAnalyzableContent ? '' : 'Add post text, an image, or analyze a video first.',
    }
  }, [
    isInternalRole,
    isKambiOrg,
    aiCapabilities.features?.bet_suggestions?.enabled,
    aiReview.suggestedBets,
    aiReview.suggestedBetsUnmatched,
    aiReview.suggestedBetsWarnings,
    aiReview.suggestedBetsSource,
    suggestBetsStatus,
    formData.kambiOutcomeIds,
    hasAnalyzableContent,
    handleSelectKambiResult,
    findSuggestedBets,
    setAiReview,
  ])

  const suggestTagsMutation = useMutation({
    mutationFn: ({ orgSlug, text }) => aiApi.suggestTags(orgSlug, text),
    onSuccess: (response, request) => {
      if (latestTagSuggestionContextRef.current !== request.contextKey) return
      const parsed = parseAiJson(response.response)
      setAiReview((currentValue) => ({ ...currentValue, tags: parsed?.tags || [] }))
    },
    onError: (error, request) => {
      if (latestTagSuggestionContextRef.current === request.contextKey) {
        toast.error(error.response?.data?.error || 'Failed to suggest tags')
      }
    },
  })
  const suggestTags = suggestTagsMutation.mutate

  const savePromptPresetMutation = useMutation({
    mutationFn: async ({ name, isDefault }) => {
      if (!currentOrg?.slug) throw new Error('No organization selected')
      if (isDefault) {
        await Promise.all(
          imagePromptPresets
            .filter((preset) => preset.defaults?.ai_image_prompt?.is_default)
            .map((preset) => mediaLibraryApi.updateTemplate(currentOrg.slug, preset.id, {
              ...aiPromptTemplatePayload(preset.name, aiPromptFieldsFromTemplate(preset), aiPromptTextFromTemplate(preset), false),
              description: preset.description || null,
            }))
        )
      }
      return mediaLibraryApi.createTemplate(currentOrg.slug, aiPromptTemplatePayload(name, imagePromptFields, imagePrompt, isDefault))
    },
    onSuccess: (template) => {
      toast.success('AI prompt preset saved')
      setPromptPresetNameRequest(null)
      setSelectedPromptPresetId(template?.id || '')
      queryClient.invalidateQueries({ queryKey: ['post-creator-ai-prompt-presets', currentOrg?.slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to save prompt preset'),
  })

  const uploadReferenceImage = useCallback(
    (file, fields) => mediaLibraryApi.uploadAsset(currentOrg.slug, file, fields),
    [currentOrg?.slug],
  )
  const deleteReferenceImage = useCallback(
    (id) => mediaLibraryApi.deleteAsset(currentOrg.slug, id),
    [currentOrg?.slug],
  )
  const referenceImageState = useReferenceImages({
    orgSlug: currentOrg?.slug,
    value: referenceImages,
    onChange: setReferenceImages,
    uploadFile: uploadReferenceImage,
    deleteAsset: deleteReferenceImage,
  })

  const generateImageMutation = useImageGenerationJob({
    scope: draftKey,
    job: imageGenerationJob,
    setJob: setImageGenerationJob,
    buildInput: (size = DEFAULT_AI_IMAGE_SIZE) => {
      const prompt = imagePrompt.trim()
      if (!prompt) throw new Error('Add prompt direction before generating an image')
      return {
        prompt,
        size,
        promptFields: normalizeAiPromptFields(imagePromptFields),
        presetId: selectedPromptPresetId || null,
        referenceAssetIds: referenceImageState.items.filter((reference) => reference.status === 'ready').map((reference) => reference.id),
      }
    },
    createJob: ({ prompt, size, referenceAssetIds }) => aiApi.createImageJob(currentOrg.slug, {
      prompt,
      size,
      context_type: 'post_draft',
      ...(referenceAssetIds.length ? { reference_asset_ids: referenceAssetIds } : {}),
    }),
    getJob: (id) => aiApi.getJob(currentOrg.slug, id),
    complete: async (completedJob, { prompt, size, promptFields, presetId, referenceAssetIds }) => {
      const url = completedJob.result?.url
      if (!url) throw new Error('Image generation returned no asset URL')
      const asset = await mediaLibraryApi.createAsset(currentOrg.slug, {
        asset_type: 'image',
        title: aiAssetTitle(promptFields, aiPromptFallbackName(promptFields, prompt)),
        source_kind: 'ai_generated',
        status: 'ready',
        url,
        object_key: completedJob.result?.object_key || null,
        metadata: {
          ai: {
            prompt_fields: promptFields,
            compiled_prompt: prompt,
            size,
            preset_id: presetId,
            job_id: completedJob.id,
            parent_asset_id: null,
            versions: [],
            ...(referenceAssetIds.length ? { reference_asset_ids: referenceAssetIds } : {}),
          },
        },
      })
      return { response: completedJob, asset, compiledPrompt: prompt, promptFields, imageSize: size }
    },
    onSuccess: (result) => setGeneratedImageReview({ ...result, sourceAsset: null }),
    onError: (error) => toast.error(imageGenerationErrorMessage(error)),
  })

  const editImageMutation = useMutation({
    mutationFn: async () => {
      if (!generatedImageReview?.asset?.url) throw new Error('No generated image selected')
      if (!revisionPrompt.trim()) throw new Error('Describe the revision to make')

      const response = await aiApi.editImage(currentOrg.slug, {
        image_url: generatedImageReview.asset.url,
        prompt: revisionPrompt.trim(),
        size: generatedImageReview.imageSize || DEFAULT_AI_IMAGE_SIZE,
        context_type: 'post_draft',
      })
      const url = response?.url || response?.image_url || response?.data?.[0]?.url
      if (!url) {
        throw new Error('Image revision returned no asset URL')
      }

      const parentAsset = generatedImageReview.asset
      const parentAi = parentAsset.metadata?.ai || {}
      const nextVersion = (parentAi.versions?.length || 0) + 1
      const asset = await mediaLibraryApi.createAsset(currentOrg.slug, {
        asset_type: 'image',
        title: `${parentAsset.title || aiAssetTitle(generatedImageReview.promptFields)} revision ${nextVersion}`,
        source_kind: 'ai_generated',
        status: 'ready',
        url,
        object_key: response?.object_key || null,
        metadata: {
          ai: {
            prompt_fields: generatedImageReview.promptFields,
            compiled_prompt: generatedImageReview.compiledPrompt,
            revision_prompt: revisionPrompt.trim(),
            size: generatedImageReview.imageSize || DEFAULT_AI_IMAGE_SIZE,
            preset_id: selectedPromptPresetId || parentAi.preset_id || null,
            model: response?.model || null,
            job_id: response?.job_id || null,
            parent_asset_id: parentAsset.id,
            versions: [
              ...(parentAi.versions || []),
              {
                asset_id: parentAsset.id,
                prompt: parentAi.revision_prompt || parentAi.compiled_prompt || generatedImageReview.compiledPrompt,
                created_at: parentAsset.created_at || new Date().toISOString(),
              },
            ],
          },
        },
      })

      return { response, asset }
    },
    onSuccess: ({ response, asset }) => {
      setGeneratedImageReview((currentValue) => ({
        ...currentValue,
        response,
        asset,
        sourceAsset: currentValue?.asset || null,
      }))
      setRevisionPrompt('')
      queryClient.invalidateQueries({ queryKey: ['post-creator-library-assets', currentOrg?.slug] })
      toast.success('Image revision ready')
    },
    onError: (error) => toast.error(imageGenerationErrorMessage(error, 'Failed to revise image')),
  })

  const queueVideoAnalysis = async ({
    mediaId,
    mediaUrl,
    title,
    contentType,
  }) => {
    const entry = {
      orgSlug: currentOrg.slug,
      mediaUrl,
      title,
      contentType,
      startedAt: Date.now(),
    }


    try {
      const response = await aiApi.createTranscriptionJob(currentOrg.slug, {
        media_url: mediaUrl,
        title,
        content_type: contentType,
        context_type: 'post_draft',
      })
      const job = response?.job
      if (!job?.id) throw new Error('Transcription job was not created')
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return

      const nextEntry = createVideoAnalysisFromJob(job, entry)
      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: nextEntry,
      }))
      if (job.status === 'completed') {
        setAiReview((currentValue) => mergeTranscriptionOutput(currentValue, job.result || {}))
      }
    } catch (error) {
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return
      setTranscriptionJobs((currentValue) => ({
        ...currentValue,
        [mediaId]: { ...entry, ...createVideoAnalysisFromError(error) },
      }))
    }
  }

  const handleMediaUpload = (files) => {
    mediaUploads.enqueue(files, {
      discard: (asset) => { mediaLibraryApi.deleteAsset(currentOrg.slug, asset.id).catch(() => {}) },
      upload: (file, mediaType, options) => mediaLibraryApi.uploadAsset(currentOrg.slug, file, { title: file.name, asset_type: mediaType, content_type: mediaUploadContentType(file), save_to_library: saveUploadsToLibrary }, options),
      attach: (id, mediaType, file, asset) => {
        removedUploadPlaceholderIdsRef.current.delete(id)
        setUploadedMedia((current) => current.map((media) => media.id === id ? {
          ...media,
          mediaObjectId: id,
          mediaId: asset.id,
          objectKey: asset.object_key || null,
          url: asset.url,
        } : media))
        setFormData((current) => ({
          ...current,
          mediaObjects: current.mediaObjects.some((media) => media.id === id) ? current.mediaObjects : [...current.mediaObjects, {
            id,
            mediaObjectId: id,
            mediaId: asset.id,
            object_key: asset.object_key || null,
            type: mediaType,
            url: asset.url,
            name: file.name,
          }],
        }))
      },
      analyze: async (id, file, asset) => {
        let capabilities = aiCapabilitiesQuery.data
        if (!capabilities) {
          try {
            capabilities = await queryClient.fetchQuery({ queryKey: ['post-creator-ai-capabilities', currentOrg.slug], queryFn: () => aiApi.getCapabilities(currentOrg.slug) })
          } catch { /* Leave the uploaded file available when AI cannot be reached. */ }
        }
        const capability = capabilities?.features?.transcription
        if (capability?.enabled !== true) {
          setTranscriptionJobs((current) => ({ ...current, [id]: { orgSlug: currentOrg.slug, ...createUnavailableVideoAnalysis(capability?.reason) } }))
          return
        }
        await queueVideoAnalysis({ mediaId: id, mediaUrl: asset.url, title: file.name, contentType: mediaUploadContentType(file) })
      },
    })
  }

  const handleRetryTranscription = async (mediaId) => {
    const entry = transcriptionJobs[mediaId]
    if (
      !currentOrg?.slug
      || !isRetryableVideoAnalysis(entry)
      || retryingTranscriptionMediaIdsRef.current.has(mediaId)
    ) return

    retryingTranscriptionMediaIdsRef.current.add(mediaId)
    setTranscriptionJobs((currentValue) => {
      const currentEntry = currentValue[mediaId]
      if (!isRetryableVideoAnalysis(currentEntry)) return currentValue
      return {
        ...currentValue,
        [mediaId]: { ...currentEntry, status: 'retrying', error: '' },
      }
    })

    try {
      const response = await aiApi.retryJob(currentOrg.slug, entry.jobId)
      const job = response?.job
      if (!job?.id) throw new Error('Transcription retry did not return a job ID')
      if (removedUploadPlaceholderIdsRef.current.has(mediaId)) return

      const nextEntry = createVideoAnalysisFromJob(job, {
        ...entry,
        orgSlug: currentOrg.slug,
        startedAt: Date.now(),
      })
      setTranscriptionJobs((currentValue) => {
        if (!currentValue[mediaId]) return currentValue
        return { ...currentValue, [mediaId]: nextEntry }
      })
      if (job.status === 'completed') {
        setAiReview((currentValue) => mergeTranscriptionOutput(currentValue, job.result || {}))
      }
    } catch (error) {
      setTranscriptionJobs((currentValue) => {
        const currentEntry = currentValue[mediaId]
        if (!currentEntry) return currentValue
        return {
          ...currentValue,
          [mediaId]: {
            ...currentEntry,
            ...createVideoAnalysisFromError(error),
          },
        }
      })
    } finally {
      retryingTranscriptionMediaIdsRef.current.delete(mediaId)
    }
  }

  const handleCaptionTracksChange = (media, tracks) => {
    setMediaCaptionTracks(media.mediaObjectId || media.id, tracks, { setUploadedMedia, setFormData })
  }

  const handleCaptionTrackUpload = async (media, file) => {
    const validationError = validateCaptionUploadFile(file)
    if (validationError) {
      toast.error(`Caption upload failed: ${validationError}`)
      return
    }
    const mediaKey = media.mediaObjectId || media.id
    const setCaptionUploading = (captionUploading) => {
      setUploadedMedia((current) => current.map((item) => (
        (item.mediaObjectId || item.id) === mediaKey ? { ...item, captionUploading } : item
      )))
    }
    setCaptionUploading(true)
    try {
      const caption = await mediaLibraryApi.uploadCaptionTrack(currentOrg.slug, file)
      if (!caption?.url) throw new Error('Failed to upload caption file')
      const lang = captionLangFromFilename(file.name)
      const track = createCaptionTrack({
        url: caption.url,
        objectKey: caption.object_key || null,
        fileName: file.name,
        lang,
        label: lang ? captionLabelForLang(lang) : '',
        kind: 'subtitles',
      })
      // Append functionally: the operator may have edited other tracks
      // while this file uploaded.
      setMediaCaptionTracks(mediaKey, (tracks) => [...tracks, track], { setUploadedMedia, setFormData })
    } catch (error) {
      toast.error(`Caption upload failed: ${extractErrorMessage(error)}`)
    } finally {
      setCaptionUploading(false)
    }
  }

  const removeMedia = (index) => {
    const media = uploadedMedia[index]
    if (!media) return
    mediaUploads.remove(media.id)
    removedUploadPlaceholderIdsRef.current.add(media.id)
    const removedJob = transcriptionJobs[media.id]
    const hasOtherCompletedAnalysis = uploadedMedia.some((item, itemIndex) => (
      itemIndex !== index && transcriptionJobs[item.id]?.status === 'completed'
    ))
    removeMediaAtIndex(index, { uploadedMedia, setUploadedMedia, setFormData })
    setTranscriptionJobs((currentValue) => {
      if (!currentValue[media.id]) return currentValue
      const nextValue = { ...currentValue }
      delete nextValue[media.id]
      return nextValue
    })
    if (isImageMedia(media)) {
      setAiReview((currentValue) => ['image', 'video+image'].includes(currentValue.suggestedBetsSource)
        ? { ...currentValue, suggestedBets: [], suggestedBetsUnmatched: [], suggestedBetsWarnings: [], suggestedBetsSource: '', generatedAt: null }
        : currentValue)
    }
    if (removedJob?.status === 'completed' && !hasOtherCompletedAnalysis) {
      setAiReview(createComposerAiReview())
    }
  }

  const organizationNameForSlug = (organizationSlug) => (
    userOrgs.find((organization) => organization.slug === organizationSlug)?.name || organizationSlug
  )

  // Flags the regulations under Where it runs and, when the rest can still
  // publish, offers to narrow; the server re-checks the confirmed list.
  const handleRegulationShortfall = (availability, { requestedTargetOfferings, kambiOutcomeIds, skipApproval }) => {
    const organizationSlug = availability.organizationSlug
    const isCurrentOrganization = !organizationSlug || organizationSlug === currentOrg?.slug
    const organizationName = isCurrentOrganization ? '' : organizationNameForSlug(organizationSlug)
    if (isCurrentOrganization) {
      setRegulationAvailability({ ...availability, kambiOutcomeIds })
    }
    if (composerMode === 'one-page') {
      window.requestAnimationFrame(() => document.getElementById('post-composer-section-distribution')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
    }
    if (availability.partialAllowed) {
      // Switch steps without moving focus: the confirmation takes it.
      setWorkflowError(null)
      setActiveStep('distribution')
      setPartialPublishPrompt({ availability, isCurrentOrganization, organizationName, requestedTargetOfferings, skipApproval })
      return
    }
    const message = regulationShortfallMessage(availability, { labelFor: regulationLabel, organizationName })
    changeStep('distribution')
    showWorkflowError({ step: 'distribution', message })
    toast.error(message)
  }

  const submitPost = async (skipApproval, {
    allowPartialRegulations = false,
    requestedTargetOfferings = null,
    targetOfferings = null,
  } = {}) => {
    if (!creatorSelectionValid) {
      const creatorIssue = {
        step: 'content',
        message: 'Wait for the selected creator to finish loading, or clear it before publishing.',
      }
      changeStep(creatorIssue.step)
      showWorkflowError(creatorIssue)
      if (composerMode === 'one-page') {
        window.requestAnimationFrame(() => document.getElementById('post-composer-section-content')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
      }
      return
    }

    if (hasUnfinishedMediaUploads(uploadedMedia) || uploadedMedia.some((media) => media.captionUploading)) {
      const uploadIssue = { step: 'content', message: uploadedMedia.some((media) => media.uploading || media.captionUploading)
        ? 'Wait for media uploads to finish before saving the post.'
        : 'Retry, replace, or remove unfinished files before saving the post.' }
      changeStep(uploadIssue.step)
      showWorkflowError(uploadIssue)
      if (composerMode === 'one-page') {
        window.requestAnimationFrame(() => document.getElementById('post-composer-section-content')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
      }
      return
    }

    const captionIssueMessage = describeCaptionTrackIssues(uploadedMedia)
    if (captionIssueMessage) {
      const captionIssue = { step: 'content', message: captionIssueMessage }
      changeStep(captionIssue.step)
      showWorkflowError(captionIssue)
      if (composerMode === 'one-page') {
        window.requestAnimationFrame(() => document.getElementById('post-composer-section-content')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
      }
      return
    }

    const finalMarketValidationError = marketValidationError
    const finalIssue = POST_COMPOSER_SECTION_REGISTRY
      .map((entry) => validatePostComposerStep(entry.id, formData, { hasEnabledSurfaces, marketValidationError: finalMarketValidationError }))
      .find(Boolean) || validatePostComposerStep('submit', formData, { hasEnabledSurfaces })
    if (finalIssue) {
      changeStep(finalIssue.step)
      showWorkflowError(finalIssue)
      if (composerMode === 'one-page') {
        window.requestAnimationFrame(() => document.getElementById(`post-composer-section-${finalIssue.step}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }))
      }
      return
    }

    const submittedFormData = { ...formData, skipApproval: Boolean(skipApproval) }
    setFormData(submittedFormData)
    // A confirmed narrowing only reaches the payload, so a failed publish leaves
    // Where it runs as the operator chose it; the original list stays target_offerings.
    const submissionFormData = targetOfferings ? { ...submittedFormData, targetOfferings } : submittedFormData
    const sentTargetOfferings = requestedTargetOfferings ?? submittedFormData.targetOfferings
    const regulationConfirmation = allowPartialRegulations
      ? { allowPartialRegulations: true, requestedTargetOfferings: sentTargetOfferings }
      : {}
    const updatePayload = isEditMode
      ? buildFeedUpdatePayload(submissionFormData, {
          providerKind,
          baselineFormData: initialEditFormDataRef.current,
          ...regulationConfirmation,
        })
      : null
    const restrictedOfferingIds = (updatePayload?.target_offerings || [])
      .filter((offeringId) => creatorOfferingRestrictions.disabledIds.includes(offeringId))
    if (restrictedOfferingIds.length > 0) {
      const labels = restrictedOfferingIds
        .map((offeringId) => regulationDisplayLabel(offeringId, regulationLookup(offeringId)))
        .join(', ')
      const message = `Remove ${labels} from Where it runs before changing bet or regulation details.`
      changeStep('distribution')
      showWorkflowError({ step: 'distribution', message })
      toast.error(message)
      return
    }

    // Resolves which surface a new post will actually land on: the first
    // explicitly submitted ID, or — when surfaceIds is null/omitted —
    // the API's own default (the first enabled, include_by_default
    // catalog surface, never a surface merely named "default-feed").
    // Computed once so this pre-submit guard and the deferred-video queue
    // redirect below agree on the same target. Manage Posts always scopes
    // to a concrete surface, so a post that resolves to zero surfaces has
    // nowhere reachable to send the operator (or the post) to; block it
    // here instead of creating an orphaned post.
    const explicitSurfaceIds = Array.isArray(submissionFormData.surfaceIds)
      ? submissionFormData.surfaceIds
      : null
    const launchCatalog = launchSurfacesQuery.data?.items || []
    const catalogDefaultSurfaceId = explicitSurfaceIds
      ? null
      : (launchCatalog.find((surface) => surface.is_enabled && surface.include_by_default)?.id || null)
    const effectiveTargetSurfaceId = explicitSurfaceIds
      ? (explicitSurfaceIds[0] || null)
      : catalogDefaultSurfaceId
    if (!isEditMode && !effectiveTargetSurfaceId) {
      const message = 'Configure at least one feed surface before publishing.'
      changeStep('distribution')
      showWorkflowError({ step: 'distribution', message })
      toast.error(message)
      return
    }


    setIsSubmitting(true)
    setModerationBlock(null)

    try {
      if (isKambiOrg && formData.kambiOutcomeIds.length > 0) {
        let latestValidation
        try {
          latestValidation = marketValidationRef.current?.revalidate
            ? await marketValidationRef.current.revalidate()
            : kambiValidation
          if (!latestValidation) throw new Error('Provider validation did not return a result')
          setKambiValidation(latestValidation)
        } catch (error) {
          changeStep('market')
          showWorkflowError({
            step: 'market',
            message: 'Provider validation failed before publish. Your other work is preserved; retry or change the market.',
          })
          toast.error(extractErrorMessage(error))
          return
        }

        // Ask before the round trip when validation already shows the gap;
        // only a request that re-resolves the market qualifies.
        const resolvesMarket = !isEditMode || Array.isArray(updatePayload?.kambi_outcome_ids)
        const validatedShortfall = !allowPartialRegulations && resolvesMarket
          ? validationRegulationShortfall(latestValidation, submissionFormData.targetOfferings, submissionFormData.kambiOutcomeIds)
          : null
        if (validatedShortfall) {
          handleRegulationShortfall(validatedShortfall, {
            requestedTargetOfferings: sentTargetOfferings,
            kambiOutcomeIds: submissionFormData.kambiOutcomeIds,
            skipApproval,
          })
          return
        }
      }

      if (isEditMode) {
        const payload = updatePayload

        let data = null
        if (Object.keys(payload).length > 0) {
          data = await postApi.update(currentOrg.slug, postIdParam, payload)
        }

        submissionCompletedRef.current = true
        clearDraft()
        queryClient.invalidateQueries({ queryKey: ['posts', currentOrg?.slug] })
        queryClient.invalidateQueries({ queryKey: ['feed-ops'] })
        const skippedSentence = describeSkippedResponse(data, { labelFor: regulationLabel, organizationNameFor: organizationNameForSlug })
        // Changes moderation holds are not live: say so, and land on every
        // book state, where the post is still listed once it leaves the feed.
        const editNotice = moderationEditNotice(data)
        if (editNotice) {
          const notify = editNotice.tone === 'error' ? toast.error : toast.info
          notify([editNotice.message, skippedSentence].filter(Boolean).join(' '))
          const editSurfaceSlug = explicitSurfaceIds?.length
            ? launchCatalog.find((surface) => surface.id === explicitSurfaceIds[0])?.slug || null
            : null
          navigate(editSurfaceSlug
            ? `/dashboard/feed/posts?book_state=all&surface=${encodeURIComponent(editSurfaceSlug)}`
            : '/dashboard/feed/posts?book_state=all')
          return
        }
        const message = ['Post updated successfully!', skippedSentence].filter(Boolean).join(' ')
        // Manage posts never reads location.state, so skipped regulations also go out as a toast.
        if (skippedSentence) toast.success(message)
        navigate('/dashboard/feed/posts', { state: { message } })
      } else {
        const payload = buildFeedCreatePayload(submissionFormData, {
          providerKind,
          ...regulationConfirmation,
        })

        const data = await postApi.create(currentOrg.slug, payload)
        const skippedSentence = describeSkippedResponse(data, { labelFor: regulationLabel, organizationNameFor: organizationNameForSlug })

        const hasVideoMedia = submissionFormData.mediaObjects.some((media) => media.type === 'video')
        if (hasVideoMedia) {
          toast.info('Video is being transcoded and will appear in the feed shortly.')
        }

        const approvalStatus = data?.approval_status || (submissionFormData.skipApproval ? 'approved' : 'pending_approval')
        // A newly queued video cannot be projected into feed_items yet, so an
        // approved create response legitimately comes back unpublished with
        // denorm_status pending while the transcode finishes. That specific
        // shape is accepted processing, not a failure; every other approved
        // response that is not published/completed still fails loudly. A
        // video still awaiting review is not approved at all, so approval
        // status is checked first: a pending-review video must stay labeled
        // pending, never approved.
        const isDeferredVideoPublication =
          approvalStatus === 'approved' && hasVideoMedia && data?.is_published === false && data?.denorm_status === 'pending'
        // In enforce mode an approved post waits unpublished, publish intent
        // kept, until its moderation check or a brand review clears it. That
        // is a hold, not a failed publication.
        const moderationNotice = approvalStatus === 'approved' && data?.is_published === false
          ? moderationPublishNotice(data)
          : null
        if (
          approvalStatus === 'approved' &&
          !isDeferredVideoPublication &&
          !moderationNotice &&
          (data?.is_published !== true || data?.denorm_status !== 'completed')
        ) {
          throw new Error('Post approval completed, but publication did not finish. Your draft was preserved.')
        }

        // A regulation only gets its own caption once the post it forks from
        // exists, so overrides are applied after create. Failing here leaves a
        // published post with the shared caption everywhere, which the
        // Manage-posts inspector can still fix, so it must not fail the create.
        const contentOverrides = Object.entries(regulationCopy)
          .filter(([, override]) => regulationOverrideDiffers(override, submissionFormData.text, submissionFormData.tagIds))
        // A fork is its own posts row with its own approval_status, and approving
        // the root does not approve it. Forking a regulation off a post that is
        // still pending review would therefore move that regulation onto a row
        // nobody publishes, leaving it with no post at all. A deferred video's
        // root has not finished projecting either, so its media still carries
        // pending transcode metadata that the API refuses to clone. Gating on
        // the server's actual response (not the client's requested
        // skipApproval) matters: if the server ever declines to honor a
        // publish-now request, forking here would still try to clone a post
        // that was never approved.
        const publicationCompleted = approvalStatus === 'approved' && data?.is_published === true && data?.denorm_status === 'completed'
        if (data?.id && contentOverrides.length > 0 && publicationCompleted) {
          try {
            for (const [offeringId, override] of contentOverrides) {
              const forked = await feedOpsApi.createVariants(currentOrg.slug, data.id, {
                offering_ids: [offeringId],
              })
              const variantPostId = forked?.variants?.[0]?.post_id
              if (!variantPostId) continue
              // The clone already carries the root's caption and tags, and the
              // update keys off nil vs empty, so send only what actually
              // differs. Sending an unchanged tag set would rewrite post_tags
              // for no reason.
              const patch = { scope: 'post' }
              if ((override.text || '') !== (submissionFormData.text || '')) {
                patch.text = override.text
              }
              if (!sameTagSet(override.tagIds || [], submissionFormData.tagIds || [])) {
                patch.tag_ids = override.tagIds || []
              }
              await feedOpsApi.updatePost(currentOrg.slug, variantPostId, patch)
            }
          } catch (variantError) {
            toast.error(`Post created, but its regulation-specific content did not save: ${extractErrorMessage(variantError)}. Add it from Manage posts.`)
          }
        } else if (contentOverrides.length > 0 && moderationNotice) {
          toast.info('Regulation-specific content was not applied because moderation is holding this post. Add it from Manage posts once it is live.')
        } else if (contentOverrides.length > 0 && isDeferredVideoPublication) {
          toast.info('Regulation-specific content was not applied because the video is still processing. Add it from Manage posts once processing finishes.')
        } else if (contentOverrides.length > 0) {
          toast.info('Regulation-specific content was not applied because this post went to review. Add it from Manage posts once it is approved.')
        }

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['posts', currentOrg?.slug] }),
          queryClient.invalidateQueries({ queryKey: ['postCount', currentOrg?.slug] }),
          queryClient.invalidateQueries({ queryKey: ['feed-ops'] }),
        ])
        submissionCompletedRef.current = true
        clearDraft()
        if (moderationNotice || isDeferredVideoPublication) {
          // The post is approved but not yet published, so it belongs in the
          // all-book-state approved queue rather than the "active" (live)
          // tab it will only reach once moderation clears it or the video
          // finishes processing; there its row carries the moderation badge.
          // The pre-submit guard above already guarantees
          // effectiveTargetSurfaceId is set for a create-mode submission, so
          // the queue this operator is sent to always names a concrete surface.
          const targetSurfaceSlug = launchCatalog.find((surface) => surface.id === effectiveTargetSurfaceId)?.slug || null
          const approvedQueuePath = targetSurfaceSlug
            ? `/dashboard/feed/posts?book_state=all&status=approved&surface=${encodeURIComponent(targetSurfaceSlug)}`
            : '/dashboard/feed/posts?book_state=all&status=approved'
          // Nothing on the destination route reads location.state, so a
          // message stashed there is silently dropped. Surface it through
          // the same app-level toast used for every other outcome instead.
          if (moderationNotice) {
            const notify = moderationNotice.tone === 'error' ? toast.error : toast.info
            notify([moderationNotice.message, skippedSentence].filter(Boolean).join(' '))
          } else {
            toast.success(['Post approved. It will go live once video processing finishes.', skippedSentence].filter(Boolean).join(' '))
          }
          navigate(approvedQueuePath)
        } else {
          const destinationTab = approvalStatus === 'approved' ? 'active' : 'pending'
          const successMessage = [
            `Post moved to ${approvalStatus === 'approved' ? 'Live' : 'Pending Review'}.`,
            skippedSentence,
          ].filter(Boolean).join(' ')
          if (skippedSentence) toast.success(successMessage)
          navigate(`/dashboard/feed/posts?tab=${destinationTab}`, {
            state: { message: successMessage },
          })
        }
      }
    } catch (error) {
      const availability = parseRegulationAvailabilityError(error)
      const blocked = parseModerationBlock(error)
      if (blocked) {
        // Keep the text the refusal was computed against: finding offsets
        // count into it, and the editor may keep typing.
        setModerationBlock({
          ...blocked,
          values: {
            text: submissionFormData.text,
            title: submissionFormData.title,
            subtitle: submissionFormData.subtitle,
            cta_label: submissionFormData.ctaLabel,
            cta_target: submissionFormData.ctaTarget,
          },
        })
        toast.error(blocked.message)
      } else if (availability) {
        handleRegulationShortfall(availability, {
          requestedTargetOfferings: sentTargetOfferings,
          kambiOutcomeIds: submissionFormData.kambiOutcomeIds,
          skipApproval,
        })
      } else {
        toast.error(extractErrorMessage(error))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const confirmPartialPublish = async () => {
    const prompt = partialPublishPrompt
    if (!prompt) return
    await submitPost(prompt.skipApproval, {
      allowPartialRegulations: true,
      requestedTargetOfferings: prompt.requestedTargetOfferings,
      // An additional organization's list isn't this post's; it narrows server-side.
      targetOfferings: prompt.isCurrentOrganization ? prompt.availability.available : null,
    })
    // A second refusal replaces the prompt with fresh lists; keep that one.
    setPartialPublishPrompt((current) => (current === prompt ? null : current))
  }

  const cancelPartialPublish = () => {
    const prompt = partialPublishPrompt
    setPartialPublishPrompt(null)
    if (!prompt) return
    showWorkflowError({
      step: 'distribution',
      message: regulationShortfallMessage(prompt.availability, {
        labelFor: regulationLabel,
        organizationName: prompt.organizationName,
      }),
    })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (composerMode === 'guided' && activeStep !== 'distribution') {
      handleContinue()
      return
    }
    // An implicit submit follows the visible primary action: review for
    // Wager staff, direct publish for everyone else.
    submitPost(isEditMode ? formData.skipApproval : (!canSendToReview && canPublishNow))
  }

  const normalizedPostText = formData.text.trim()
  const postTextLength = normalizedPostText.length
  const anyTranscriptionPolling = useMemo(
    () => Object.values(transcriptionJobs).some((entry) => entry?.status === 'polling' || entry?.status === 'retrying'),
    [transcriptionJobs]
  )
  const textSuggestionsCapability = aiCapabilities.features?.text_suggestions
  const showCaptionAssistant = Boolean(
    textSuggestionsCapability?.enabled
      && (postTextLength >= 12 || aiReview.captions.length > 0 || anyTranscriptionPolling)
  )
  const aiTextUnavailableReason = textSuggestionsCapability?.enabled
    ? ''
    : textSuggestionsCapability?.reason?.message || 'AI text suggestions are unavailable.'
  const captionSuggestionReason = aiTextUnavailableReason || (
    postTextLength >= 12 ? '' : 'Add at least 12 characters of post text before suggesting a caption.'
  )
  const tagSuggestionsEnabled = Boolean(textSuggestionsCapability?.enabled && postTextLength >= 12)
  const tagSuggestionContextKey = `${currentOrg?.slug || ''}\u0000${normalizedPostText}`
  latestTagSuggestionContextRef.current = tagSuggestionContextKey

  useEffect(() => {
    if (observedTagSuggestionContextRef.current !== tagSuggestionContextKey) {
      observedTagSuggestionContextRef.current = tagSuggestionContextKey
      requestedTagSuggestionContextRef.current = ''
    }
    if (!tagSuggestionsEnabled || !currentOrg?.slug) return undefined
    if (requestedTagSuggestionContextRef.current === tagSuggestionContextKey) return undefined

    const timeoutId = window.setTimeout(() => {
      if (latestTagSuggestionContextRef.current !== tagSuggestionContextKey) return
      requestedTagSuggestionContextRef.current = tagSuggestionContextKey
      suggestTags({
        orgSlug: currentOrg.slug,
        text: normalizedPostText,
        contextKey: tagSuggestionContextKey,
      })
    }, TAG_SUGGESTION_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [currentOrg?.slug, normalizedPostText, suggestTags, tagSuggestionContextKey, tagSuggestionsEnabled])

  const runTextAiAction = (reason, mutation, variables) => {
    if (reason) {
      toast.error(reason)
      return
    }
    mutation.mutate(variables)
  }
  const handlePostTextChange = (value) => {
    setFormData((previousValue) => ({ ...previousValue, text: value }))
    setAiReview((currentValue) => (
      currentValue.tags.length > 0 ? { ...currentValue, tags: [] } : currentValue
    ))
  }


  const handleExternalCaptionApply = (caption) => {
    if (!caption) return
    handlePostTextChange(caption)
  }

  const handleImagePromptChange = (value) => {
    setImagePrompt(value)
    setSelectedPromptPresetId('')
  }

  const handleImagePromptFieldChange = (field, value) => {
    setImagePromptFields((currentValue) => ({
      ...currentValue,
      [field]: value,
    }))
    setSelectedPromptPresetId('')
  }

  const handleBuildPromptFromFields = () => {
    const compiledPrompt = buildImagePrompt(imagePromptFields)
    if (!compiledPrompt.trim()) {
      toast.error('Fill in at least one guided prompt field first')
      return
    }
    setImagePrompt(compiledPrompt)
  }

  const handlePromptPresetChange = (presetId) => {
    setSelectedPromptPresetId(presetId)
    const preset = imagePromptPresets.find((item) => item.id === presetId)
    if (preset) {
      setImagePromptFields(aiPromptFieldsFromTemplate(preset))
      setImagePrompt(aiPromptTextFromTemplate(preset))
    }
  }

  const handleSavePromptPreset = (isDefault = false) => {
    if (!imagePrompt.trim() && !buildImagePrompt(imagePromptFields).trim()) {
      toast.error('Add a prompt or guided prompt details before saving')
      return
    }
    const fallbackName = aiPromptFallbackName(imagePromptFields, imagePrompt, selectedPromptPreset?.name)
    setPromptPresetNameRequest({ isDefault, initialName: fallbackName })
  }

  const confirmPromptPresetName = (name) => {
    if (!promptPresetNameRequest) return
    const { isDefault } = promptPresetNameRequest
    savePromptPresetMutation.mutate({ name, isDefault })
  }

  const useGeneratedAsset = () => {
    if (!generatedImageReview?.asset) return
    attachAssetToComposer(generatedImageReview.asset)
    if (generatedImageReview.asset.id) {
      setGeneratedAssetIds((current) => Array.from(new Set([...(current || []), generatedImageReview.asset.id])))
    }
    setGeneratedImageReview(null)
  }

  const discardGeneratedAsset = () => {
    setGeneratedImageReview(null)
    setRevisionPrompt('')
  }


  if (loading) {
    return <div className={composerStyles.form}><p>Loading post...</p></div>
  }

  if (loadError) {
    const backToPosts = (
      <Button variant="secondary" onClick={() => navigate('/dashboard/feed/posts')}>
        Back to Manage posts
      </Button>
    )
    return (
      <div className={composerStyles.form}>
        {loadError.kind === 'not_found' ? (
          <StatePanel
            state="search-empty"
            title="Post not found"
            description="This post doesn't exist in this organization, or it has been deleted."
            action={backToPosts}
          />
        ) : (
          <StatePanel
            state="error"
            title="This post could not load"
            description={loadError.message}
            onRetry={() => {
              setLoading(true)
              setLoadAttempt((attempt) => attempt + 1)
            }}
            action={backToPosts}
          />
        )}
      </div>
    )
  }

  const mediaSection = (
    <div>
      <MediaSection
        uploadedMedia={uploadedMedia}
        onMediaUpload={handleMediaUpload}
        onRetryUpload={mediaUploads.retry}
        onCancelUpload={mediaUploads.cancel}
        saveUploadsToLibrary={saveUploadsToLibrary}
        onSaveUploadsToLibraryChange={setSaveUploadsToLibrary}
        onRemoveMedia={removeMedia}
        libraryAssets={libraryAssets}
        assetSearch={assetSearch}
        onAssetSearchChange={setAssetSearch}
        onAttachAsset={attachAssetToComposer}
        onActivePanelChange={setMediaActivePanel}
        aiCapabilities={aiCapabilities}
        hasCaptionText={formData.text.trim().length > 0}
        imagePrompt={imagePrompt}
        imagePromptFields={imagePromptFields}
        onImagePromptChange={handleImagePromptChange}
        onImagePromptFieldChange={handleImagePromptFieldChange}
        onBuildPromptFromFields={handleBuildPromptFromFields}
        imagePromptPresets={imagePromptPresets}
        selectedPromptPresetId={selectedPromptPresetId}
        onPromptPresetChange={handlePromptPresetChange}
        onSavePromptPreset={() => handleSavePromptPreset(false)}
        onSaveDefaultPromptPreset={() => handleSavePromptPreset(true)}
        promptPresetSaving={savePromptPresetMutation.isPending}
        imagePending={generateImageMutation.isPending}
        imageStatus={imageGenerationJob}
        onGenerateImage={(imageSize) => generateImageMutation.mutate(imageSize)}
        onDiscardImage={generateImageMutation.discardJob}
        referenceImages={referenceImageState.items}
        onAddReferenceImages={referenceImageState.addFiles}
        onRemoveReferenceImage={referenceImageState.removeReference}
        referenceImagesUploading={referenceImageState.uploading}
        aiReview={aiReview}
        showTagSuggestionsInAnalysis={false}
        onPreviewImage={(url) => setPreviewMedia({ url, type: 'image' })}
        orgSlug={currentOrg?.slug}
        onExternalCaptionApply={handleExternalCaptionApply}
        isCryptoDotCom={isPredictionContractOrg}
        transcriptionJobs={transcriptionJobs}
        onRetryTranscription={handleRetryTranscription}
        onUploadCaptionTrack={handleCaptionTrackUpload}
        onChangeCaptionTracks={handleCaptionTracksChange}
      />
      {mediaActivePanel === 'library' && (hasNextLibraryAssetsPage || isFetchNextLibraryAssetsPageError || (isLibraryAssetsError && libraryAssets.length > 0)) ? (
        <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center' }}>
          {isFetchNextLibraryAssetsPageError || isLibraryAssetsError ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => fetchNextLibraryAssetsPage()}
            >
              Retry loading more
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => fetchNextLibraryAssetsPage()}
              loading={isFetchingNextLibraryAssetsPage}
              disabled={isFetchingNextLibraryAssetsPage}
            >
              Load more
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )

  const contentSection = (
    <ContentSection
      text={formData.text}
      onTextChange={handlePostTextChange}
      title={formData.title}
      subtitle={formData.subtitle}
      onTitleChange={(value) => setFormData((previousValue) => ({ ...previousValue, title: value }))}
      onSubtitleChange={(value) => setFormData((previousValue) => ({ ...previousValue, subtitle: value }))}
      collapseMoreOptions
      moreOptionsAttention={Boolean(
        formData.tagIds.length
        || aiReview.tags.length
        || suggestTagsMutation.isPending
      )}
      showSectionTitle={false}
      captionAction={showCaptionAssistant ? {
        onClick: (direction) => runTextAiAction(captionSuggestionReason, suggestCaptionMutation, direction),
        disabled: Boolean(captionSuggestionReason) || suggestCaptionMutation.isPending,
        pending: suggestCaptionMutation.isPending,
        reason: captionSuggestionReason,
      } : null}
      captionTools={showCaptionAssistant ? {
        guidance: captionGuidance,
        onGuidanceChange: setCaptionGuidance,
        suggestions: aiReview.captions,
        onUseSuggestion: handleExternalCaptionApply,
        transcriptionPending: anyTranscriptionPolling && aiReview.captions.length === 0,
      } : null}
      creatorSlot={!isEditMode ? (
        <CreatorPicker
          value={formData.userId}
          selectedCreator={selectedCreator}
          onChange={(creatorId, creator) => {
            setFormData((previousValue) => ({ ...previousValue, userId: creatorId }))
            setSelectedCreator(normalizeSelectedCreator(creator))
          }}
          onResolvedCreator={handleResolvedCreator}
          onValidityChange={handleCreatorValidityChange}
          allowCreate={canCreateCreators}
        />
      ) : null}
      tagsSlot={(
        <TagSelector
          selectedTagIds={formData.tagIds}
          onChange={(ids) => setFormData((previousValue) => ({ ...previousValue, tagIds: ids }))}
          suggestedTags={aiReview.tags}
          suggestionsEnabled={tagSuggestionsEnabled}
          suggestionsPending={suggestTagsMutation.isPending}
        />
      )}
    />
  )

  const ctaSection = (
    <CtaSection
      isCryptoDotCom={isCryptoDotComOrg}
      isOG={isOGOrg}
      ctaKind={formData.ctaKind}
      ctaLabel={formData.ctaLabel}
      ctaTarget={formData.ctaTarget}
      onChange={(field, value) => setFormData((previousValue) => ({ ...previousValue, [field]: value }))}
      embedded
    />
  )

  const marketStep = (
    <MarketStep
      role={currentOrg?.role}
      regulationMappings={kambiOfferingsData?.mappings}
      searchOfferings={searchScope.offerings}
      searchScope={{ value: searchScope.value, options: searchScope.options, all: searchScope.all, onChange: setMarketSearchScopeChoice }}
      isKambi={isKambiOrg}
      isCryptoDotCom={isCryptoDotComOrg}
      isOG={isOGOrg}
      orgSlug={currentOrg?.slug}
      query={marketSearchQuery}
      onQueryChange={setMarketSearchQuery}
      suggestedBetsProps={suggestedBetsProps}
      selectedOutcomeIds={formData.kambiOutcomeIds}
      onSelectKambiResult={handleSelectKambiResult}
      showHeader={composerMode !== 'one-page'}
      betSectionProps={{
        selectorRef: captureMarketValidator,
        outcomeIds: formData.kambiOutcomeIds,
        targetOfferings: formData.targetOfferings,
        onOutcomeIdsChange: (ids) => {
          setFormData((previousValue) => ({
            ...previousValue,
            kambiOutcomeIds: ids,
            wagerEntries: reconcileAuthoredWagerEntries(previousValue.wagerEntries, ids, { preferParlay: true }),
          }))
        },
        onValidationResult: handleKambiValidationResult,
        wagerEntries: formData.wagerEntries,
        onWagerEntriesChange: (entries) => setFormData((previousValue) => ({ ...previousValue, wagerEntries: entries })),
        wagerValidationError: authoredWagerValidationError,
        validationResult: kambiValidation,
        selectedCreator,
        selectionIds: formData.selectionIds,
        onSelectionIdsChange: handleSelectionsChange,
        providerSelectionIds: formData.providerSelectionIds,
        onProviderSelectionIdsChange: (ids) => handleProviderSelectionIdsChange(ids),
        onSelectedProviderSelectionsChange: (selections) => handleProviderSelectionIdsChange(
          selections.map((selection) => selection.external_id).filter(Boolean),
          selections,
        ),
      }}
    />
  )

  const publishToSection = (
    <div data-testid="publish-to-section">
      <PublishToSection
        orgSlug={currentOrg?.slug}
        surfaceIds={formData.surfaceIds}
        onChange={(nextIds) => setFormData((previousValue) => ({ ...previousValue, surfaceIds: nextIds }))}
        onAvailabilityChange={handleSurfaceAvailabilityChange}
        onInitialSurfaceApplied={handleInitialSurfaceApplied}
        initialSurfaceSlug={isEditMode ? '' : initialSurfaceSlug}
        canConfigureSurface={canConfigureSurface}
      />
    </div>
  )

  const settingsSection = (
    <AdvancedSettingsSection
      values={formData}
      isEditMode={isEditMode}
      expanded={advancedSectionExpanded}
      onToggle={setAdvancedSectionExpanded}
      onChange={(field, value) => setFormData((previousValue) => ({ ...previousValue, [field]: value }))}
      onToggleChange={handleToggleChange}
      showRanking={isInternalRole}
      ctaChildren={ctaSection}
    />
  )

  const regulationCopySection = canTailorRegulationCopy ? (
    <RegulationContentSection
      regulations={regulationChoices}
      tags={allTags}
      sharedText={formData.text}
      sharedTagIds={formData.tagIds}
      overrides={regulationCopy}
      onChange={setRegulationCopy}
      disabled={isSubmitting}
    />
  ) : null

  const regulationTargetSection = isKambiOrg ? (
    <RegulationTargetSection
      regulations={regulationTargets}
      selected={formData.targetOfferings || []}
      disabledReasonByOfferingId={creatorOfferingRestrictions.reasonById}
      noticeByOfferingId={regulationNotices}
      onToggle={handleToggleRegulation}
      onSelectAll={() => setTargetOfferings(enabledOfferings)}
      onClear={() => setTargetOfferings([])}
      disabled={isSubmitting}
    />
  ) : null

  const jumpToSection = (step) => {
    setActiveStep(step)
    if (step === 'market') setOnePageBetExpanded(true)
    document.getElementById(`post-composer-section-${step}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const composerSections = {
    content: <ContentStep media={mediaSection} content={contentSection} showStepLabel={composerMode !== 'one-page'} />,
    market: marketStep,
    distribution: (
      <PublishStep
        publishTo={publishToSection}
        settings={settingsSection}
        destination={null}
        regulationTarget={regulationTargetSection}
        regulationCopy={regulationCopySection}
        showStepLabel={composerMode !== 'one-page'}
      />
    ),
  }

  const stepContent = composerMode === 'one-page' ? (
    <div className={composerStyles.onePageComposer}>
      <nav className={composerStyles.sectionJumpBar} aria-label="Post sections">
        {POST_COMPOSER_SECTION_REGISTRY.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={composerStyles.sectionJumpBtn}
            aria-current={activeStep === entry.id ? 'page' : undefined}
            onClick={() => jumpToSection(entry.id)}
          >
            {sectionLabel(entry, 'individual')}
          </button>
        ))}
      </nav>
      {renderComposerSections({
        registry: POST_COMPOSER_SECTION_REGISTRY,
        mode: composerMode,
        activeStep,
        sections: composerSections,
        marketExpanded: onePageBetExpanded,
        onToggleMarketExpanded: setOnePageBetExpanded,
      })}
    </div>
  ) : renderComposerSections({
    registry: POST_COMPOSER_SECTION_REGISTRY,
    mode: composerMode,
    activeStep,
    sections: composerSections,
  })
  const hasFutureSchedule = Boolean(formData.scheduledFor && Date.parse(formData.scheduledFor) > Date.now())
  const publishNowLabel = hasFutureSchedule ? 'Schedule publish' : 'Publish now'
  const hasPendingMediaUploads = hasUnfinishedMediaUploads(uploadedMedia) || uploadedMedia.some((media) => media.captionUploading)
  const showTerminalActions = composerMode === 'one-page' || activeStep === 'distribution'

  const pendingDraftSurfaceIds = Array.isArray(pendingDraft?.formData?.surfaceIds)
    ? pendingDraft.formData.surfaceIds
    : null
  const launchSurfaceReady = Boolean(
    launchSurface
    && launchSurfacesQuery.isSuccess
    && !launchSurfacesQuery.isFetching,
  )
  const draftSurfaceMismatch = Boolean(
    launchSurfaceReady
    && pendingDraftSurfaceIds
    && (
      pendingDraftSurfaceIds.length !== 1
      || pendingDraftSurfaceIds[0] !== launchSurface.id
    ),
  )
  const launchSurfaceContextPending = Boolean(
    initialSurfaceSlug
    && pendingDraftSurfaceIds
    && (launchSurfacesQuery.isPending || launchSurfacesQuery.isFetching),
  )

  const draftBanner = pendingDraft ? (
    <div className={composerStyles.draftBanner} role="status">
      <div>
        <strong>Resume your last draft?</strong>
        <p>We found a saved draft from {new Date(pendingDraft.savedAt).toLocaleString()}.</p>
        {draftSurfaceMismatch ? (
          <p>
            This draft targets different surfaces. Restore Draft keeps those targets;
            Restore to {launchSurfaceLabel} keeps the draft content and switches its target.
          </p>
        ) : null}
        {launchSurfaceContextPending ? (
          <p>The launch surface is still loading. Restoring now keeps the draft&apos;s saved targets.</p>
        ) : null}
      </div>
      <div className={composerStyles.draftBannerActions}>
        <Button variant="secondary" onClick={discardDraft}>Discard</Button>
        {draftSurfaceMismatch ? (
          <Button variant="secondary" onClick={() => restoreDraft([launchSurface.id])}>
            Restore to {launchSurfaceLabel}
          </Button>
        ) : null}
        <Button onClick={() => restoreDraft()}>Restore Draft</Button>
      </div>
    </div>
  ) : null

  const currentStepIndex = POST_COMPOSER_SECTION_REGISTRY.findIndex((entry) => entry.id === activeStep)
  const workflowSteps = buildWorkflowSteps(POST_COMPOSER_SECTION_REGISTRY, currentStepIndex, 'individual')

  const editor = (
    <form
      onSubmit={handleSubmit}
      onKeyDown={blockImplicitFormSubmit}
      className={`${composerStyles.form} ${composerStyles.boundedMobileForm}`}
    >
      <div className={composerStyles.composerFields} data-testid="composer-scroll-region">
        {workflowError ? (
          <ErrorSummary ref={workflowErrorRef} title="This step needs attention">
            <p>{workflowError.message}</p>
          </ErrorSummary>
        ) : null}
        <ModerationStatusPanel
          orgSlug={currentOrg?.slug}
          postId={isEditMode ? postIdParam : null}
          block={moderationBlock}
          values={{ text: formData.text, title: formData.title, subtitle: formData.subtitle, cta_label: formData.ctaLabel, cta_target: formData.ctaTarget }}
          media={uploadedMedia}
          showUnenforced={isInternalRole}
        />
        {stepContent}
      </div>
      <FormActions align="between" className={composerStyles.publishActionsBar}>
        <Button
          ref={mobilePreviewTriggerRef}
          type="button"
          variant="secondary"
          className={composerStyles.mobilePreviewAction}
          onClick={() => setMobilePreviewOpen(true)}
        >
          Preview
        </Button>
        <Button variant="secondary" onClick={() => navigate('/dashboard/feed/posts')}>Cancel</Button>
        {composerMode === 'guided' && currentStepIndex > 0 ? <Button variant="secondary" onClick={handleBack}>Back</Button> : null}
        {composerMode === 'guided' && !showTerminalActions ? <Button key="continue" onClick={handleContinue}>Continue</Button> : null}
        {showTerminalActions && isEditMode ? (
          <Button type="button" loading={isSubmitting} disabled={!hasEnabledSurfaces || !creatorSelectionValid || isSubmitting || hasPendingMediaUploads} onClick={() => submitPost(formData.skipApproval)}>
            Save changes
          </Button>
        ) : null}
        {showTerminalActions && !isEditMode ? (
          <>
            {canSendToReview ? (
              <Button type="button" variant="secondary" loading={isSubmitting && !formData.skipApproval} disabled={!hasEnabledSurfaces || !creatorSelectionValid || isSubmitting || hasPendingMediaUploads} onClick={() => submitPost(false)}>
                Send to review
              </Button>
            ) : null}
            {canPublishNow ? (
              <Button type="button" loading={isSubmitting && formData.skipApproval} disabled={!hasEnabledSurfaces || !creatorSelectionValid || isSubmitting || hasPendingMediaUploads} onClick={() => submitPost(true)}>
                {publishNowLabel}
              </Button>
            ) : null}
          </>
        ) : null}
      </FormActions>
    </form>
  )

  const previewPanel = (
    <PostPreviewPanel
      branding={brandingData}
      item={previewPayload}
      mobileOpen={mobilePreviewOpen}
      onMobileOpen={() => setMobilePreviewOpen(true)}
      onMobileClose={() => setMobilePreviewOpen(false)}
      mobileTriggerRef={mobilePreviewTriggerRef}
    />
  )

  const composerTitle = isEditMode ? 'Edit post' : 'Create post'

  return (
    <>
      <PostComposerLayout
        title={composerTitle}
        subtitle={launchedForParlay && initialSurfaceSlug
          ? `Building a parlay for ${launchSurfaceLabel}`
          : undefined}
        breadcrumbs={(
          <Breadcrumbs
            items={[
              { label: 'Posts', href: '/dashboard/feed/posts' },
              { label: composerTitle },
            ]}
            renderLink={(item) => <Link to={item.href}>{item.label}</Link>}
          />
        )}
        actions={(
          <SegmentedControl
            label="Composer layout"
            value={composerMode}
            onValueChange={changeComposerMode}
            items={[
              { value: 'guided', label: 'Guided' },
              { value: 'one-page', label: 'One page' },
            ]}
          />
        )}
        steps={composerMode === 'guided' ? workflowSteps : undefined}
        currentStep={activeStep}
        onStepChange={changeStep}
        draftBanner={draftBanner}
        editor={editor}
        preview={previewPanel}
      />
      <ModalShell
        open={!!generatedImageReview}
        onClose={discardGeneratedAsset}
        title="Generated image"
        description="Review the image at full size before attaching it to the post."
        size="media"
        contentClassName={composerStyles.aiReviewModalContent}
        footer={(
          <>
            <Button variant="secondary" onClick={discardGeneratedAsset}>
              Discard
            </Button>
            <Button variant="secondary" onClick={() => toast.success('Saved to library')}>
              Save to library
            </Button>
            <Button onClick={useGeneratedAsset}>
              Use in post
            </Button>
          </>
        )}
      >
        {generatedImageReview ? (
          <div className={composerStyles.aiReviewLayout}>
            <div className={composerStyles.aiReviewImageFrame}>
              <img src={generatedImageReview.asset.url} alt={generatedImageReview.asset.title || 'Generated image'} />
            </div>
            <aside className={composerStyles.aiReviewInspector}>
              <section>
                <h3>Prompt summary</h3>
                <dl className={composerStyles.aiPromptSummary}>
                  {Object.entries(generatedImageReview.promptFields || {}).filter(([, value]) => String(value || '').trim()).map(([key, value]) => (
                    <div key={key}>
                      <dt>{key.replace(UPPERCASE_LETTERS_PATTERN, ' $1')}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
              <section>
                <h3>Revise with AI</h3>
                <FormField
                  label="Revision prompt"
                  type="textarea"
                  rows={4}
                  value={revisionPrompt}
                  onChange={(event) => setRevisionPrompt(event.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => editImageMutation.mutate()}
                  disabled={editImageMutation.isPending || !revisionPrompt.trim()}
                >
                  {editImageMutation.isPending ? 'Revising...' : 'Revise with AI'}
                </Button>
              </section>
            </aside>
          </div>
        ) : null}
      </ModalShell>

      {partialPublishPrompt ? (
        <ConfirmDialog
          open
          title="Publish to fewer regulations?"
          message={partialPublishMessage(partialPublishPrompt.availability, {
            labelFor: regulationLabel,
            organizationName: partialPublishPrompt.organizationName,
          })}
          confirmLabel={partialPublishConfirmLabel(partialPublishPrompt.availability, {
            organizationName: partialPublishPrompt.organizationName,
          })}
          cancelLabel="Go back"
          loading={isSubmitting}
          onConfirm={confirmPartialPublish}
          onCancel={cancelPartialPublish}
        />
      ) : null}

      {promptPresetNameRequest ? (
        <PromptPresetNameDialog
          open
          isDefault={promptPresetNameRequest.isDefault}
          initialName={promptPresetNameRequest.initialName}
          saving={savePromptPresetMutation.isPending}
          onConfirm={confirmPromptPresetName}
          onCancel={() => setPromptPresetNameRequest(null)}
        />
      ) : null}

      <MediaPreviewModal media={previewMedia} onClose={() => setPreviewMedia(null)} />
    </>
  )
}

function PostCreator({ mode = 'create' }) {
  return <StandardPostCreator mode={mode} />
}

export default PostCreator
